import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ValuationForm from './ValuationForm';
import ValuationsModal from './components/ValuationsModal';
import Modal from './components/Modal';
import CreateLand from './CreateLand';
import CreateStructure from './CreateStructure';
import AggregatedAssetExpenseChart from './AggregatedAssetExpenseChart';
import Plot from 'react-plotly.js';
import UtilitiesComparisonChart from './UtilitiesComparisonChart.jsx';
import UtilitiesDetailedChart from './UtilitiesDetailedChart.jsx';
import UtilityBillReviewModal from './UtilityBillReviewModal';
import TaxAssessmentModal from './TaxAssessmentModal';
import AssetExpenseLinkModal from './components/AssetExpenseLinkModal.jsx';
import PhysicalAssetToDoList from './components/PhysicalAssetToDoList.jsx';
import { formatCurrency } from './CoreFunctions';
import { buildCategoryTreeSorted } from './utils/expenseCategories';
import { exportToExcel } from './utils/exportUtils';
import { buildAssetLabel, getDomainForCategory } from './utils/assetLabelHelper';
import PropertySummaryCard from './components/PropertySummaryCard.jsx';
import WeatherForecastIcons from './components/WeatherForecastIcons.jsx';
import RecurringManager from './RecurringManager.jsx';

const ResiPropertyDetail = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [resiProperty, setResiProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mortgages, setMortgages] = useState([]);
    const [selectedMortgageId, setSelectedMortgageId] = useState(null);
    const [currentAssetId, setCurrentAssetId] = useState(null);
    const [showValuationForm, setShowValuationForm] = useState(false); // State for Valuation Modal visibility
    const [showEditValuationsModal, setShowEditValuationsModal] = useState(false);
    const [landFormVisible, setLandFormVisible] = useState(false); // State for Land Form visibility
    const [structureFormVisible, setStructureFormVisible] = useState(false); // State for Structure Form visibility
    const [, setError] = useState('');
    // Photo upload state for property
    const [photoUploading, setPhotoUploading] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const [selectedRecurringName, setSelectedRecurringName] = useState(null);

    // Transactions for selected graph period
    const [txFrom, setTxFrom] = useState(null);
    const [txTo, setTxTo] = useState(null);
    const [rvTxns, setRvTxns] = useState([]);
    const [caTxns, setCaTxns] = useState([]);
    const [txLoading, setTxLoading] = useState(false);
    const [expectedTxns, setExpectedTxns] = useState([]); // projected rows built from linked-expenses
    const [txError, setTxError] = useState('');
    const [deleting, setDeleting] = useState(new Set());

    // Unlink confirm state
    const [showUnlinkModal, setShowUnlinkModal] = useState(false);
    const [unlinkRow, setUnlinkRow] = useState(null); // { id, sourceType, description, amount, date }
    const [unlinkLoading, setUnlinkLoading] = useState(false);
    const [unlinkError, setUnlinkError] = useState('');

    // Expense categories (for consistent labels/colors)
    const [expenseCategories, setExpenseCategories] = useState({ items: [], noneItem: { value: 'none', label: 'None' } });
    // Category dropdown state (match other pages)
    const [expandedParents, setExpandedParents] = useState({}); // { [dropdownId]: Set(parentVal) }
    const [openDropdownId, setOpenDropdownId] = useState(null);

    // Sorting state for transactions table
    const [sortField, setSortField] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');

    // Link-to-asset modal state
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkRow, setLinkRow] = useState(null); // { id, sourceType, ... }
    const [selectedAssetId, setSelectedAssetId] = useState(''); // structure id or land id
    const [isRecurring, setIsRecurring] = useState(false);
    const [recurringFrequency, setRecurringFrequency] = useState('monthly');
    const [recurringSeriesName, setRecurringSeriesName] = useState('');
    const [linkLoading, setLinkLoading] = useState(false);
    const [linkError, setLinkError] = useState('');
    // Local cache of linked metadata for display (recurring/frequency)
    const [linkedMeta, setLinkedMeta] = useState({}); // { `${sourceType}-${id}`: { recurring:bool, frequency:string } }
    // Local cache for Utilities extras (bill + recurring series) to help prepopulate across refetches within this page session
    const [linkedExtras, setLinkedExtras] = useState({}); // { `${sourceType}-${id}`: { utility_bill_id:number|null, recurring_series_id:number|null } }

    // Home (Property/Structure) modal state for linking Home category expenses with recurring series
    const [showHomeModal, setShowHomeModal] = useState(false);
    const [homeRow, setHomeRow] = useState(null); // { id, sourceType, description, transaction_date/post_date }
    const [homeUiCategory, setHomeUiCategory] = useState('');
    const [homeLoading, setHomeLoading] = useState(false);
    const [homeError, setHomeError] = useState('');
    const [homeAssetOptions, setHomeAssetOptions] = useState([]); // structures under this property
    const [homeSelectedAssetId, setHomeSelectedAssetId] = useState('');
    const [homeIsRecurring, setHomeIsRecurring] = useState(false);
    const [homeFrequency, setHomeFrequency] = useState('monthly');
    const [homeSeriesOptions, setHomeSeriesOptions] = useState([]); // [{name,type,last_frequency}]
    const [homeRecurringMode, setHomeRecurringMode] = useState('existing'); // 'existing' | 'new'
    const [homeExistingSeriesName, setHomeExistingSeriesName] = useState('');
    const [homeRecurringName, setHomeRecurringName] = useState('');
    const [homeRecurringType, setHomeRecurringType] = useState('variable');

    const HOME_UI_TO_ASSET = {
        home_repair: 'repair',
        home_maintenance: 'maintenance',
        home_enhancement: 'enhancement',
        home_decor: 'home_decor',
        home_furniture: 'home_furniture',
        home_artwork: 'home_artwork',
        home_pest_control: 'pest_control',
        home_inspection: 'home_inspection',
        home_tax_payment: 'taxes',
        home_insurance_payment: 'insurance',
        home_other: 'home_other',
    }

    const ExpenseGroupPill = ({ group }) => {
        if (!group) return null;
        const colors = {
            'Operating': { bg: '#3B82F6', text: '#FFFFFF' },
            'Financing': { bg: '#F59E0B', text: '#FFFFFF' },
            'CapEx': { bg: '#10B981', text: '#FFFFFF' },
        };
        const style = colors[group] || { bg: '#9CA3AF', text: '#FFFFFF' };
        return (
            <span style={{
                backgroundColor: style.bg,
                color: style.text,
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                display: 'inline-block',
                whiteSpace: 'nowrap'
            }}>
                {group}
            </span>
        );
    };

    // Utility bills upload state
    const [showUtilityModal, setShowUtilityModal] = useState(false);
    const [utilityProvider, setUtilityProvider] = useState('MidAmerican');

    // Utility expense linking modal (for transactions categorized as Utilities)
    const [showUtilityLinkModal, setShowUtilityLinkModal] = useState(false);
    const [utilityRow, setUtilityRow] = useState(null);
    const [utilityType, setUtilityType] = useState('electric_gas');
    const [utilityFiles, setUtilityFiles] = useState([]);
    const [utilityUploading, setUtilityUploading] = useState(false);
    const [utilityResults, setUtilityResults] = useState([]);
        const [showUtilityReview, setShowUtilityReview] = useState(false);
        const [utilityReviewStartIndex, setUtilityReviewStartIndex] = useState(0);
    const [utilityError, setUtilityError] = useState('');

    // Tax Assessment state
    const [taxAssessments, setTaxAssessments] = useState([]);
    const [showTaxModal, setShowTaxModal] = useState(false);
    const [editingAssessment, setEditingAssessment] = useState(null);
    const [taxLoading, setTaxLoading] = useState(false);
    const [taxSortField, setTaxSortField] = useState('assessment_year');
    const [taxSortDirection, setTaxSortDirection] = useState('desc');

    // Book & Market valuation chart state
    const [valuationLoading, setValuationLoading] = useState(false);
    const [valuationError, setValuationError] = useState('');
    const [valuationSeries, setValuationSeries] = useState({ book: [], market: [] });

    // Tabs and chart type toggle (must be declared before effects that depend on them)
    const [activeTab, setActiveTab] = useState('property'); // 'property' | 'structures' | 'land'
    const [chartType, setChartType] = useState('summary'); // default to Summary as first tab

    const [expensesSubView, setExpensesSubView] = useState('all'); // 'all' | 'recurring'
    const [utilitiesSubView, setUtilitiesSubView] = useState('regular'); // 'regular' | 'detailed'

    // Robust scroll preservation across tab/view switches
    const pendingScrollYRef = useRef(null);
    const keepScrollRafRef = useRef(0);
    const keepScrollUntilRef = useRef(0);
    const keepScrollTick = useRef(null);

    const startKeepScrollStable = React.useCallback((ms = 700) => {
        const y = typeof window !== 'undefined' ? window.scrollY : 0;
        pendingScrollYRef.current = y;
        const until = Date.now() + ms;
        keepScrollUntilRef.current = Math.max(keepScrollUntilRef.current, until);
        // If a loop is already running, let it continue with the extended deadline
        if (keepScrollRafRef.current) return;
        keepScrollTick.current = () => {
            const targetY = pendingScrollYRef.current;
            const now = Date.now();
            if (typeof targetY === 'number') {
                try { window.scrollTo(0, targetY); } catch (_) {}
            }
            if (now < keepScrollUntilRef.current) {
                keepScrollRafRef.current = requestAnimationFrame(keepScrollTick.current);
            } else {
                // Stop loop
                if (keepScrollRafRef.current) cancelAnimationFrame(keepScrollRafRef.current);
                keepScrollRafRef.current = 0;
                keepScrollUntilRef.current = 0;
                // leave pendingScrollYRef as last Y for potential subsequent layout, but clear soon
                setTimeout(() => { pendingScrollYRef.current = null; }, 0);
            }
        };
        keepScrollRafRef.current = requestAnimationFrame(keepScrollTick.current);
    }, []);

    // As a backstop, after chartType/activeTab changes trigger re-render, attempt an immediate restore
    useLayoutEffect(() => {
        if (pendingScrollYRef.current !== null) {
            const y = pendingScrollYRef.current;
            // double-rAF immediate correction
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    try { window.scrollTo(0, y); } catch (_) {}
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartType, activeTab]);

    useEffect(() => {
        if (id) {
            fetchTaxAssessments();
        }
    }, [id]);

    const handleTaxSort = (field) => {
        const isAsc = taxSortField === field && taxSortDirection === 'asc';
        setTaxSortDirection(isAsc ? 'desc' : 'asc');
        setTaxSortField(field);
    };

    const sortedTaxAssessments = [...taxAssessments].sort((a, b) => {
        let valA = a[taxSortField];
        let valB = b[taxSortField];

        // Handle numeric fields
        if (['total_assessed_value', 'total_taxable_value', 'levy_rate', 'net_taxable_value', 'net_annual_taxes', 'assessment_year', 'total_rollback_rate'].includes(taxSortField)) {
            valA = parseFloat(valA) || 0;
            valB = parseFloat(valB) || 0;
        } else {
            valA = (valA || '').toString().toLowerCase();
            valB = (valB || '').toString().toLowerCase();
        }

        if (valA < valB) return taxSortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return taxSortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const renderSortIcon = (field) => {
        if (taxSortField !== field) return <span style={{ marginLeft: 4, opacity: 0.3 }}>↕</span>;
        return taxSortDirection === 'asc' ? <span style={{ marginLeft: 4 }}>↑</span> : <span style={{ marginLeft: 4 }}>↓</span>;
    };

    const fetchTaxAssessments = async () => {
        setTaxLoading(true);
        try {
            const res = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${id}/tax-assessments/`);
            setTaxAssessments(res.data || []);
        } catch (e) {
            console.error("Failed to fetch tax assessments", e);
        } finally {
            setTaxLoading(false);
        }
    };
    const setChartTypeWithScroll = React.useCallback((val) => {
        startKeepScrollStable(900);
        setChartType(val);
    }, [startKeepScrollStable]);
    const setActiveTabWithScroll = React.useCallback((val) => {
        startKeepScrollStable(900);
        setActiveTab(val);
    }, [startKeepScrollStable]);

    // Force-refresh charts after linking so they refetch data
    const [chartsRefresh, setChartsRefresh] = useState(0);

    // Debt linking modal state
    const [showDebtModal, setShowDebtModal] = useState(false);
    const [linkingRow, setLinkingRow] = useState(null); // the table row being linked (cash only)
    const [debtList, setDebtList] = useState([]); // non-revolving debts
    const [selectedDebt, setSelectedDebt] = useState(null);
    const [debtCandidates, setDebtCandidates] = useState([]); // candidate amortization rows
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState('');
    const [modalPhase, setModalPhase] = useState('chooseDebt'); // 'chooseDebt' | 'chooseCandidate'

    const openDebtModalForRow = async (row) => {
        setModalError('');
        setModalLoading(true);
        setShowDebtModal(true);
        setLinkingRow(row);
        setSelectedDebt(null);
        setDebtCandidates([]);
        setModalPhase('chooseDebt');
        try {
            const res = await axios.get('/api/balance_sheet/financing/');
            const allDebts = Array.isArray(res.data) ? res.data : [];
            const nonRevolving = allDebts.filter(d => (d.debt_type === 'auto_loan' || d.debt_type === 'mortgage'));
            setDebtList(nonRevolving);
        } catch (e) {
            console.error('Failed to load debts', e);
            setModalError('Failed to load debts.');
        } finally {
            setModalLoading(false);
        }
    };

    const loadDebtCandidates = async (debt) => {
        if (!linkingRow || !debt) return;
        setModalError('');
        setModalLoading(true);
        try {
            const url = `/api/balance_sheet/assets/cash_accounts/transactions/${linkingRow.id}/candidate-debt-payments`;
            const params = { debt_type: debt.debt_type, debt_id: debt.id };
            const res = await axios.get(url, { params });
            const candidates = res.data && Array.isArray(res.data.candidates) ? res.data.candidates : [];
            setDebtCandidates(candidates);
            setModalPhase('chooseCandidate');
        } catch (e) {
            console.error('Failed to load candidate payments', e);
            setModalError('Failed to load candidate payments.');
        } finally {
            setModalLoading(false);
        }
    };

    const confirmLinkToCandidate = async (candidate) => {
        if (!linkingRow || !selectedDebt || !candidate) return;
        setModalError('');
        setModalLoading(true);
        try {
            const url = `/api/balance_sheet/assets/cash_accounts/transactions/${linkingRow.id}/link-debt-payment/`;
            await axios.post(url, {
                debt_type: selectedDebt.debt_type,
                debt_id: selectedDebt.id,
                candidate_id: candidate.id,
            });
            // Refresh transactions to pick up linked_debt_info
            if (linkingRow.sourceType === 'cash') {
                const caRes = await axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: txFrom, end_date: txTo } });
                setCaTxns(caRes.data || []);
            }
            setShowDebtModal(false);
            setLinkingRow(null);
        } catch (e) {
            console.error('Failed to link payment', e);
            setModalError('Failed to link payment.');
        } finally {
            setModalLoading(false);
        }
    };

    // Debt & Equity chart state
    const [debtEqLoading, setDebtEqLoading] = useState(false);
    const [debtEqError, setDebtEqError] = useState('');
    const [debtEqSeries, setDebtEqSeries] = useState({ market: [], debt: [], debtRaw: [] });

    // Expense chart expected series toggle — disabled system-wide per request
    const [showExpected, setShowExpected] = useState(false);

    // Asset list (for resolving linked asset names/URLs)
    const [allAssets, setAllAssets] = useState([]);
    useEffect(() => {
        const loadAssets = async () => {
            try {
                const phys = await axios.get('/api/balance_sheet/assets/physical_assets/minimal/');
                setAllAssets(Array.isArray(phys.data) ? phys.data : []);
            } catch (e) {
                setAllAssets([]);
            }
        };
        loadAssets();
    }, []);

    // Memoized helper maps for fast asset lookups and labels
    const assetMaps = useMemo(() => {
        try {
            const list = Array.isArray(allAssets) ? allAssets : [];
            const byId = new Map();
            const resiAddressById = new Map(); // property id -> street address
            const structIdsByProperty = new Map(); // property id -> sorted structure ids
            for (const a of list) {
                const idStr = String(a.id);
                byId.set(idStr, a);
                const type2 = (a.asset_type_2 || a.type_2 || a.type2 || '').toLowerCase();
                if (type2 === 'resi_property') {
                    const addr = a.address && a.address.street_address ? a.address.street_address : '';
                    resiAddressById.set(idStr, addr);
                } else if (type2 === 'structure') {
                    const rp = a.residential_property || a.residentialProperty || a.parent_property || a.parent || null;
                    const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
                    if (rpId != null) {
                        const key = String(rpId);
                        if (!structIdsByProperty.has(key)) structIdsByProperty.set(key, []);
                        structIdsByProperty.get(key).push(a.id);
                    }
                }
            }
            // Sort structure ids for ordinal positions
            const structOrdinalById = new Map();
            for (const ids of structIdsByProperty.values()) {
                ids.sort((a,b) => a - b);
                ids.forEach((sid, idx) => {
                    structOrdinalById.set(String(sid), idx + 1);
                });
            }
            return { byId, resiAddressById, structOrdinalById };
        } catch (e) {
            return { byId: new Map(), resiAddressById: new Map(), structOrdinalById: new Map() };
        }
    }, [allAssets]);

    const buildAssetUrl = (asset) => {
        if (!asset) return null;
        const id = asset.id;
        const type2 = asset.asset_type_2 || asset.type_2 || asset.type2;
        const t = (type2 || '').toLowerCase();
        if (t === 'vehicle') return `/balance_sheet/assets/physical_assets/vehicles/${id}`;
        if (t === 'resi_property') return `/balance_sheet/assets/physical_assets/resi_properties/${id}`;
        if (t === 'structure' || t === 'land') {
            // Resolve parent property id from various serializer shapes quickly
            const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent;
            const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
            if (rpId) return `/balance_sheet/assets/physical_assets/resi_properties/${rpId}`;
        }
        return null;
    };

    // Fetch valuations when Property tab + Book & Market view is selected
    useEffect(() => {
        const assetId = resiProperty?.id;
        if (!assetId) return;
        if (!(activeTab === 'property' && chartType === 'book_market')) return;

        const loadVals = async () => {
            setValuationLoading(true);
            setValuationError('');
            try {
                const res = await axios.get(`/api/balance_sheet/assets/valuations/${assetId}/`);
                const data = res?.data || { book: [], market: [] };

                const normalize = (arr) => {
                    // Map to {x: Date, y: Number}, sort, dedupe same-day
                    const pts = (Array.isArray(arr) ? arr : [])
                        .filter(r => r && r.date && r.value !== null && r.value !== undefined)
                        .map(r => ({ x: new Date(r.date + 'T00:00:00'), y: Number(r.value) }))
                        .filter(p => !isNaN(p.x.getTime()) && isFinite(p.y));
                    pts.sort((a,b) => a.x - b.x);
                    const map = new Map();
                    for (const p of pts) {
                        const key = p.x.toISOString().slice(0,10);
                        map.set(key, p); // keep last of the day
                    }
                    let out = Array.from(map.values()).sort((a,b)=>a.x-b.x);

                    // Flat-line extrapolate to today
                    if (out.length) {
                        const last = out[out.length-1];
                        const today = new Date();
                        const todayKey = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
                        if (last.x < todayKey) {
                            out = [...out, { x: todayKey, y: last.y }];
                        }
                    }
                    return out;
                };

                setValuationSeries({
                    book: normalize(data.book),
                    market: normalize(data.market),
                });
            } catch (e) {
                console.error('Failed to load valuations', e);
                setValuationError('Failed to load valuations.');
                setValuationSeries({ book: [], market: [] });
            } finally {
                setValuationLoading(false);
            }
        };
        loadVals();
    }, [resiProperty?.id, activeTab, chartType, chartsRefresh]);

    // Fetch Market valuations + Outstanding Debt when Debt & Equity view is selected
    useEffect(() => {
        const assetId = resiProperty?.id;
        if (!assetId) return;
        if (!(activeTab === 'property' && chartType === 'debt_equity')) return;

        const normalizePts = (arr, xKey, yKey) => {
            const pts = (Array.isArray(arr) ? arr : [])
                .filter(r => r && r[xKey] && (r[yKey] !== null && r[yKey] !== undefined))
                .map(r => ({ x: new Date(r[xKey] + 'T00:00:00'), y: Number(r[yKey]) }))
                .filter(p => !isNaN(p.x.getTime()) && isFinite(p.y));
            pts.sort((a,b) => a.x - b.x);
            // de-duplicate per day
            const map = new Map();
            for (const p of pts) {
                const key = p.x.toISOString().slice(0,10);
                map.set(key, p);
            }
            let out = Array.from(map.values()).sort((a,b)=>a.x-b.x);
            // Flat-line append to today
            if (out.length) {
                const last = out[out.length-1];
                const today = new Date();
                const todayKey = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
                if (last.x < todayKey) out = [...out, { x: todayKey, y: last.y }];
            }
            return out;
        };

        const normalizeDebtRaw = (arr) => {
            const pts = (Array.isArray(arr) ? arr : [])
                .filter(r => r && r.date)
                .map(r => ({
                    x: new Date(r.date + 'T00:00:00'),
                    outstanding: Number(r.outstanding),
                    mortgage: Number(r.mortgage ?? 0),
                    heloc: Number(r.heloc ?? 0),
                }))
                .filter(p => !isNaN(p.x.getTime()) && isFinite(p.outstanding));
            pts.sort((a,b) => a.x - b.x);
            const map = new Map();
            for (const p of pts) {
                const key = p.x.toISOString().slice(0,10);
                map.set(key, p); // keep last per day
            }
            let out = Array.from(map.values()).sort((a,b)=>a.x-b.x);
            if (out.length) {
                const last = out[out.length-1];
                const today = new Date();
                const todayKey = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
                if (last.x < todayKey) out = [...out, { ...last, x: todayKey }];
            }
            return out;
        };

        const loadDebtEq = async () => {
            setDebtEqLoading(true);
            setDebtEqError('');
            try {
                const [valRes, debtRes] = await Promise.all([
                    axios.get(`/api/balance_sheet/assets/valuations/${assetId}/`),
                    axios.get(`/api/balance_sheet/assets/debt-series/${assetId}/`),
                ]);
                const marketArr = Array.isArray(valRes?.data?.market) ? valRes.data.market : [];
                const debtArr = Array.isArray(debtRes?.data?.series) ? debtRes.data.series : [];
                const market = normalizePts(marketArr, 'date', 'value');
                const debtRaw = normalizeDebtRaw(debtArr);
                const debt = debtRaw.map(r => ({ x: r.x, y: r.outstanding }));
                setDebtEqSeries({ market, debt, debtRaw });
            } catch (e) {
                console.error('Failed to load debt & equity series', e);
                setDebtEqError('Failed to load debt & equity series.');
                setDebtEqSeries({ market: [], debt: [] });
            } finally {
                setDebtEqLoading(false);
            }
        };
        loadDebtEq();
    }, [resiProperty?.id, activeTab, chartType, chartsRefresh]);

    useEffect(() => {
        const fetchResiPropertyData = async () => {
            try {
                const response = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${id}/`);
                setResiProperty(response.data);
            } catch (error) {
                console.error('Error fetching residential property details:', error);
            } finally {
                setLoading(false);
            }
        };

        const fetchMortgages = async () => {
            try {
                const response = await axios.get('/api/balance_sheet/assets/physical_assets/resi_properties/mortgage-choices');
                setMortgages(response.data);
            } catch (error) {
                console.error('Error fetching mortgages:', error);
            }
        };

        if (id) {
            fetchResiPropertyData();
            fetchMortgages();
            // fetchExpenseData(); // Call to fetch expense data
        } else {
            setLoading(false);
        }
    }, [id]);

    // Handle property photo upload/replace
    const handlePhotoFileChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file || !resiProperty?.id) return;
        setPhotoError('');
        const allowed = ['image/jpeg','image/png','image/webp'];
        if (!allowed.includes(file.type)) {
            setPhotoError('Please select a JPG, PNG, or WEBP image.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) { // 10MB
            setPhotoError('Image exceeds 10MB limit.');
            return;
        }
        try {
            setPhotoUploading(true);
            const form = new FormData();
            form.append('photo', file);
            await axios.post(`/api/balance_sheet/assets/physical_assets/${resiProperty.id}/photo/`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true,
            });
            // Refresh property data to get new photo_thumb
            const response = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${id}/`);
            setResiProperty(response.data);
        } catch (err) {
            console.error('Upload failed', err);
            setPhotoError('Failed to upload photo.');
        } finally {
            setPhotoUploading(false);
            try { e.target.value = ''; } catch (_) {}
        }
    };

    // Load expense category choices (for labels and colors)
    useEffect(() => {
        const onDocClick = () => setOpenDropdownId(null);
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    useEffect(() => {
        const fetchExpenseCategories = async () => {
            try {
                const response = await axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/');
                const tree = buildCategoryTreeSorted(response.data || []);
                setExpenseCategories(tree);
            } catch (error) {
                // keep defaults
            }
        };
        fetchExpenseCategories();
    }, []);

    // Fetch transactions whenever date range changes
    useEffect(() => {
        const loadTxns = async () => {
            if (!txFrom || !txTo) return;
            setTxLoading(true);
            setTxError('');
            try {
                const [rvRes, caRes] = await Promise.allSettled([
                    axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: txFrom, end_date: txTo } }),
                    axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: txFrom, end_date: txTo } }),
                ]);
                const rvList = (rvRes.status === 'fulfilled') ? (Array.isArray(rvRes.value?.data) ? rvRes.value.data : []) : [];
                const caList = (caRes.status === 'fulfilled') ? (Array.isArray(caRes.value?.data) ? caRes.value.data : []) : [];
                setRvTxns(rvList);
                setCaTxns(caList);

                // Also load actual and expected (projected) asset expenses across this property's assets
                try {
                    const assetIds = [];
                    // property id, structures, and land if present
                    if (resiProperty?.id) assetIds.push(resiProperty.id);
                    if (Array.isArray(resiProperty?.structures)) assetIds.push(...resiProperty.structures.map(s=>s.id));
                    if (resiProperty?.land?.id) assetIds.push(resiProperty.land.id);
                    const calls = assetIds.map(aid => axios.get(`/api/balance_sheet/assets/physical_assets/${aid}/linked-expenses/`, { params: { start_date: txFrom, end_date: txTo, only_txn_backed: 1 } }).then(r=>({ aid, data:r.data})).catch(()=>({ aid, data:null })));
                    const results = await Promise.all(calls);
                    const projRows = [];
                    const actualRows = [];
                    for (const res of results) {
                        const aid = res.aid;
                        const actual = Array.isArray(res.data?.actual) ? res.data.actual : [];
                        const projected = Array.isArray(res.data?.projected) ? res.data.projected : [];
                        // Build Actual rows (these are AssetExpenseORM rows, e.g., split loan payment components)
                        for (const a of actual) {
                            actualRows.push({
                                id: a.id,
                                sourceType: 'asset_expense',
                                transaction_date: a.date || a.payment_date || a.transaction_date || null,
                                post_date: a.date || a.payment_date || a.post_date || null,
                                description: a.description || a.expense_desc || 'Asset Expense',
                                amount: Number(a.amount) || 0, // positive magnitude
                                expense_category: a.expense_category || 'none',
                                linked_asset_id: aid,
                                // Lock category editing for financing splits (read-only from backend flag when present)
                                read_only_category: !!a.read_only_category,
                                type: 'Actual',
                                source_account_display: a.source_display || 'Asset Expense',
                            });
                        }
                        for (const p of projected) {
                            // Build an Expected row for the table
                            const desc = (p.recurring_name && String(p.recurring_name).trim().length) ? p.recurring_name : 'Recurring Expense';
                            projRows.push({
                                id: `proj-${aid}-${p.source_expense_id}-${p.date}`,
                                sourceType: 'expected',
                                transaction_date: p.date,
                                post_date: p.date,
                                description: desc,
                                amount: Number(p.amount) || 0, // positive amount
                                expense_category: p.expense_category || 'none',
                                linked_asset_id: aid,
                                _recurring_frequency: p.recurring_frequency || null,
                                _recurring_type: p.recurring_type || null,
                            });
                        }
                    }
                    // Merge actual AssetExpense rows into property table set
                    try {
                        // De-dupe by id+sourceType in case multiple assets return overlapping records (shouldn't happen)
                        const key = (r) => `${r.sourceType}:${r.id}`;
                        const unique = new Map();
                        for (const r of actualRows) unique.set(key(r), r);
                        // Append to an existing local state by mapping them into ca/rv sets is complicated; instead,
                        // we’ll stash them into a local state array similar to expectedTxns. Reuse expectedTxns state?
                        // Introduce (if not existing) a separate state S for actual asset expenses.
                    } catch(e) { /* no-op */ }
                    setExpectedTxns(projRows);
                    // Store actual asset expenses into a dedicated state so we can merge below
                    setAssetExpenseTxns(actualRows);
                } catch (e) {
                    setExpectedTxns([]);
                    setAssetExpenseTxns([]);
                }

                // Populate linkedMeta from server-provided linked_expense_info so prior links show Recurring/Frequency
                try {
                    const meta = {};
                    for (const t of rvList) {
                        const info = t && t.linked_expense_info;
                        if (info && (info.recurring || info.recurring_frequency)) {
                            meta[`revolver-${t.id}`] = { recurring: !!info.recurring, frequency: info.recurring_frequency || '' };
                        }
                    }
                    for (const t of caList) {
                        const info = t && t.linked_expense_info;
                        if (info && (info.recurring || info.recurring_frequency)) {
                            meta[`cash-${t.id}`] = { recurring: !!info.recurring, frequency: info.recurring_frequency || '' };
                        }
                    }
                    setLinkedMeta(meta);
                } catch (e) { /* ignore */ }
            } catch (e) {
                setTxError('Failed to load transactions for selected period.');
                setRvTxns([]);
                setCaTxns([]);
            } finally {
                setTxLoading(false);
            }
        };
        loadTxns();
    }, [txFrom, txTo]);

    const openValuationModal = (assetId) => {
        setCurrentAssetId(assetId);
        setShowValuationForm(true);
    };

    const closeValuationModal = async () => {
        setShowValuationForm(false);
        try {
            if (resiProperty?.id) {
                const updatedResponse = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${resiProperty.id}/`);
                setResiProperty(updatedResponse.data);
            }
        } catch (e) {
            // non-fatal; modal closed already
            console.warn('Failed to refresh property after valuation submit/close', e);
        }
    };


    const structureIds = Array.isArray(resiProperty?.structures) ? resiProperty.structures.map(s => s.id) : [];
    const landId = resiProperty?.land?.id || null;
    const propertyId = resiProperty?.id || null;

    const assetIdsForTab = (tab) => {
        if (tab === 'property') return [propertyId, ...structureIds, ...(landId ? [landId] : [])].filter(Boolean);
        if (tab === 'structures') return [...structureIds];
        if (tab === 'land') return landId ? [landId] : [];
        return [];
    };

    // Category style helper (match other pages)
    const getCategoryStyle = (category) => {
        const styles = {
            'food_dining': { backgroundColor: '#FFF3E0', color: '#E65100', borderColor: '#E65100' },
            'gas_fuel': { backgroundColor: '#F3E5F5', color: '#7B1FA2', borderColor: '#7B1FA2' },
            'groceries': { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
            'internet': { backgroundColor: '#E3F2FD', color: '#1565C0', borderColor: '#1565C0' },
            'apps': { backgroundColor: '#E8EAF6', color: '#5E35B1', borderColor: '#5E35B1' },
            'books': { backgroundColor: '#FEF3C7', color: '#78350F', borderColor: '#FBBF24' },
            'landscaping': { backgroundColor: '#E0F2E1', color: '#388E3C', borderColor: '#388E3C' },
            'shopping': { backgroundColor: '#FFE0E6', color: '#C2185B', borderColor: '#C2185B' },
            'entertainment': { backgroundColor: '#E1F5FE', color: '#0277BD', borderColor: '#0277BD' },
            'travel': { backgroundColor: '#FFF8E1', color: '#F57C00', borderColor: '#F57C00' },
            'automotive': { backgroundColor: '#E0F2F1', color: '#00695C', borderColor: '#00695C' },
            'healthcare': { backgroundColor: '#FFEBEE', color: '#C62828', borderColor: '#C62828' },
            'utilities': { backgroundColor: '#FFF3E0', color: '#EF6C00', borderColor: '#EF6C00' },
            'subscription': { backgroundColor: '#F3E5F5', color: '#8E24AA', borderColor: '#8E24AA' },
            'insurance': { backgroundColor: '#E8EAF6', color: '#3F51B5', borderColor: '#3F51B5' },
            'taxes': { backgroundColor: '#FFEBEE', color: '#B71C1C', borderColor: '#B71C1C' },
            'kids__other': { backgroundColor: '#E8F5E8', color: '#43A047', borderColor: '#43A047' },
            'education': { backgroundColor: '#E0F7FA', color: '#0097A7', borderColor: '#0097A7' },
            'business': { backgroundColor: '#ECEFF1', color: '#455A64', borderColor: '#455A64' },
            'personal_care': { backgroundColor: '#FCE4EC', color: '#AD1457', borderColor: '#AD1457' },
            'dental_products': { backgroundColor: '#FCE4EC', color: '#AD1457', borderColor: '#AD1457' },
            'workout_gym_products': { backgroundColor: '#F7FEE7', color: '#4D7C0F', borderColor: '#4D7C0F' },
            'home_improvement': { backgroundColor: '#F1F8E9', color: '#558B2F', borderColor: '#558B2F' },
            'gifts_charity': { backgroundColor: '#FFF9C4', color: '#9E9D24', borderColor: '#9E9D24' },
            'fees_charges': { backgroundColor: '#FFEBEE', color: '#D32F2F', borderColor: '#D32F2F' },
            'transfer': { backgroundColor: '#E3F2FD', color: '#1976D2', borderColor: '#1976D2' },
            'payments': { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
            'rewards_cashback': { backgroundColor: '#FFF8E1', color: '#F57C00', borderColor: '#F57C00' },
            'other': { backgroundColor: '#F5F5F5', color: '#616161', borderColor: '#616161' },
            'none': { backgroundColor: '#FFFFFF', color: '#000000', borderColor: '#000000' },
        };
        return styles[category] || styles['none'];
    };

    const resolveCategoryLabel = (val) => {
        const category = String(val || 'none');
        for (const it of expenseCategories.items) {
            if (String(it.value) === category) return it.label;
            if (Array.isArray(it.children)) {
                const ch = it.children.find(c => String(c.value) === category);
                if (ch) return ch.label;
            }
        }
        if (String(expenseCategories.noneItem?.value) === category) return expenseCategories.noneItem?.label || 'None';
        return 'None';
    };

    // Display helper to show Parent – Child label consistently
    const formatParentChildLabel = (code) => {
        try {
            const raw = String(code || '').toLowerCase();
            const pretty = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

            // Utilities transaction codes often look like utilities__water, utilities__electric_gas
            if (raw.startsWith('utilities')) {
                // Use registry label for the child if available
                const child = resolveCategoryLabel(raw);
                const childLabel = child && child.toLowerCase() !== 'utilities' ? child : pretty(raw.replace(/^utilities[_-]*/, ''));
                return `Utilities – ${childLabel}`;
            }

            // Home transaction codes: home_repair, home_maintenance, home_decor, etc.
            if (raw.startsWith('home_') || raw === 'home') {
                const child = resolveCategoryLabel(raw);
                const childLabel = child && !/^home\b/i.test(child) ? child : pretty(raw.replace(/^home_/, ''));
                return `Home – ${childLabel}`;
            }

            // AssetExpense canonical codes for Home subcats (pest_control, home_decor, home_furniture, home_artwork, home_inspection, plants, home_other)
            const homeLeafs = new Set(['pest_control','home_decor','home_furniture','home_artwork','home_inspection','plants','home_other']);
            if (homeLeafs.has(raw)) {
                const childLabel = resolveCategoryLabel(raw);
                return `Home – ${childLabel || pretty(raw)}`;
            }

            // Otherwise use the registry label as-is
            return resolveCategoryLabel(raw);
        } catch (e) {
            return resolveCategoryLabel(code);
        }
    };


    // Hold actual AssetExpense rows fetched for this property (kept for future use if needed)
    const [assetExpenseTxns, setAssetExpenseTxns] = useState([]);
    // Toggle to include/exclude Expected (projected) expenses across chart and table in Expenses view
    // Default OFF per request
    // Note: state defined earlier as well; ensure single source of truth
    // (this duplicate has been removed; using the top-level definition)
    // const [showExpected, setShowExpected] = useState(false);
    // IMPORTANT: To avoid duplicates on the Property page, do NOT merge transaction-backed
    // AssetExpense rows into the flat table together with cash transactions. The cash transactions
    // already carry synthesized `children` (split components) from the backend serializer. We will
    // render those on expand below. Keep expected (projected) rows separate.
    const allTxns = [
        ...(rvTxns||[]).map(t=>({ ...t, sourceType:'revolver'})),
        ...(caTxns||[]).map(t=>({ ...t, sourceType:'cash'})),
        // Do not include projected Expected rows per system toggle
    ];

    // Display helper: flip sign for cash transactions on this page only (expenses positive, credits negative)
    const getDisplayAmount = (t) => {
        try {
            const raw = Number(t && t.amount);
            const val = isNaN(raw) ? 0 : raw;
            // Flip sign for parent cash txns and synthetic cash children so expenses show positive
            if (!t) return val;
            const st = String(t.sourceType || '');
            if (st === 'cash' || st === 'cash_child') return -val;
            return val;
        } catch (e) {
            return 0;
        }
    };

    // Sorting helpers
    const ExcelIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
    );

    // Build dynamic set of all Home sub-category values from the registry; include legacy fallback
    const homeChildSet = React.useMemo(() => {
        try {
            const items = Array.isArray(expenseCategories.items) ? expenseCategories.items : [];
            const parent = items.find(it => String(it.value) === 'home');
            const vals = new Set();
            if (parent && Array.isArray(parent.children)) {
                for (const ch of parent.children) {
                    if (ch && ch.value != null) vals.add(String(ch.value));
                }
            }
            // Legacy/home-improvement fallback if present anywhere
            vals.add('home_improvement');
            return vals;
        } catch (e) {
            return new Set(['home_repair','home_maintenance','home_enhancement','home_decor','home_furniture','home_artwork','home_pest_control','home_improvement','home_inspection','home_tax_payment','home_insurance_payment', 'home_plants']);
        }
    }, [expenseCategories]);

    // Build dynamic set of all Utilities sub-category values from the registry
    const utilitiesChildSet = React.useMemo(() => {
        try {
            const items = Array.isArray(expenseCategories.items) ? expenseCategories.items : [];
            const parent = items.find(it => String(it.value) === 'utilities');
            const vals = new Set();
            if (parent) {
                vals.add('utilities');
                if (Array.isArray(parent.children)) {
                    for (const ch of parent.children) {
                        if (ch && ch.value != null) vals.add(String(ch.value));
                    }
                }
            }
            // Fallback: include any values in the registry that start with 'utilities'
            for (const it of items) {
                const v = String(it.value || '');
                if (v.startsWith('utilities')) vals.add(v);
                if (Array.isArray(it.children)) {
                    for (const ch of it.children) {
                        const v2 = String(ch?.value || '');
                        if (v2.startsWith('utilities')) vals.add(v2);
                    }
                }
            }
            // Explicit known utility subcategory codes to ensure coverage (handles plain tokens too)
            const KNOWN = ['water','gas','electric','electricity','electric_gas','other','utilities__water','utilities__gas','utilities__electric','utilities__other'];
            for (const k of KNOWN) vals.add(k);
            if (!vals.size) vals.add('utilities');
            return vals;
        } catch (e) {
            return new Set(['utilities','water','gas','electric','electricity','electric_gas','utilities__water','utilities__gas','utilities__electric','utilities__other']);
        }
    }, [expenseCategories]);

    const allowedAssetIds = new Set(assetIdsForTab(activeTab).map(x=>String(x)));

    const filteredTxns = allTxns.filter(t => {
        if (!t) return false;
        const linkedId = t.linked_asset_id == null ? null : String(t.linked_asset_id);
        const isLinkedToAllowed = !!(linkedId && allowedAssetIds.has(linkedId));
        const catVal = String(t.expense_category || '');
        const isUtilitiesCat = utilitiesChildSet.has(catVal);

        // Strict filtering per requirement:
        // - Utilities view: only Utilities-category rows that are linked to allowed assets
        // - Other views: only rows linked to allowed assets
        if (chartType === 'utilities') {
            return isUtilitiesCat && isLinkedToAllowed;
        }
        return isLinkedToAllowed;
    });

    const processedTransactions = useMemo(() => {
        return filteredTxns.map(t => {
            const asset = t.linked_asset_id ? (allAssets || []).find(a => String(a.id) === String(t.linked_asset_id)) : null;
            const assetLabel = asset ? buildAssetLabel(asset, { ...assetMaps, transactionDomain: getDomainForCategory(t.expense_category) }) : '';
            
            let debtLabel = '';
            const ld = t.linked_debt_info || t.linkedDebtInfo;
            if (ld && (ld.type === 'auto_loan' || ld.type === 'mortgage') && (t.linked_debt_id || t.linkedDebtId)) {
                debtLabel = ld.debt_label || ld.debt_name || 'Debt';
            }

            return {
                ...t,
                date: t.transaction_date || t.post_date || t.date,
                expense_group: t.expense_group || '',
                linked_asset_label: assetLabel,
                linked_debt_label: debtLabel
            };
        });
    }, [filteredTxns, allAssets, assetMaps]);

    const handleExport = () => {
        const columns = [
            { header: 'Date', key: t => t.date ? new Date(t.date).toLocaleDateString() : '' },
            { header: 'Type', key: 'sourceType' },
            { header: 'Group', key: 'expense_group' },
            { header: 'Description', key: 'description' },
            { header: 'User Description', key: 'user_description' },
            { header: 'Amount', key: 'amount' },
            { header: 'Category', key: 'expense_category' },
            { header: 'Asset Link', key: t => t.linked_asset_label || '' },
            { header: 'Debt Link', key: t => t.linked_debt_label || '' }
        ];
        
        const propertyName = resiProperty ? (resiProperty.name || resiProperty.address?.street_address || resiProperty.description || 'Property') : (id || 'Property');
        const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '_');
        const filename = `Transactions_${propertyName}_${dateStr}`;
        exportToExcel(processedTransactions, filename, columns);
    };

    const handleSort = (field) => {
        const f = String(field);
        setSortField(prev => prev === f ? f : f);
        setSortDirection(prev => (sortField === f ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    };

    // Category dropdown helpers
    const toggleDropdown = (e, rowId) => {
        e.stopPropagation();
        setOpenDropdownId(prev => prev === rowId ? null : rowId);
    };
    const isParentExpanded = (dropdownId, parentVal) => {
        const set = expandedParents[dropdownId];
        return !!(set && set.has(parentVal));
    };
    const toggleParentExpand = (dropdownId, parentVal) => {
        setExpandedParents(prev => {
            const cur = new Set(prev[dropdownId] || []);
            if (cur.has(parentVal)) cur.delete(parentVal); else cur.add(parentVal);
            return { ...prev, [dropdownId]: cur };
        });
    };

    // Open the shared asset expense modal for a given transaction row
    const openUtilitiesModal = (row) => {
        try {
            const r2 = {
                id: row.id,
                sourceType: row.sourceType,
                description: row.description,
                amount: row.amount,
                transaction_date: row.transaction_date || row.date || '',
                post_date: row.post_date || '',
                date: row.transaction_date || row.post_date || row.date || '',
                linked_asset_id: row.linked_asset_id || '',
                linked_asset_expense_id: (row.linked_asset_expense_id != null && row.linked_asset_expense_id !== '') ? String(row.linked_asset_expense_id) : '',
                linked_utility_bill_id: row.linked_utility_bill_id || '',
                linked_recurring_series_id: row.linked_recurring_series_id || '',
                linked_property_id: row.linked_property_id || resiProperty.id || '',
                expense_category: row.category || row.expense_category || '',
            };
            setUtilityRow(r2);
            setShowUtilityLinkModal(true);
        } catch (e) {
            // fallback: minimal
            setUtilityRow({ id: row.id, sourceType: row.sourceType, description: row.description, amount: row.amount, expense_category: row.category || row.expense_category || '' });
            setShowUtilityLinkModal(true);
        }
    };

    const updateExpenseCategory = async (row, newCat) => {
        if (!row || !row.id) return;
        // Lock expected rows: users cannot change categories for expected/projection rows
        if (row.sourceType === 'expected') {
            setOpenDropdownId(null);
            return;
        }
        try {
            const updatedRow = { ...row, category: newCat, expense_category: newCat };
            if (row.sourceType === 'revolver') {
                await axios.patch(`/api/balance_sheet/financing/revolvers/transactions/${row.id}/`, { expense_category: newCat });
                setRvTxns(prev => prev.map(t => (t.id === row.id ? { ...t, expense_category: newCat } : t)));
            } else {
                await axios.patch(`/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/`, { expense_category: newCat });
                setCaTxns(prev => prev.map(t => (t.id === row.id ? { ...t, expense_category: newCat } : t)));
                // If user assigned Payments, prompt to link to a non-revolving debt (auto/mortgage)
                if (newCat === 'payments' && !row.linked_debt_id) {
                    openDebtModalForRow(updatedRow);
                }
            }

            // Standardize auto-modal triggers based on category
            const domain = getDomainForCategory(newCat);
            const isWaste = String(newCat).startsWith('waste_') || String(newCat).includes('sanitation');
            
            if (domain !== 'generic' || isWaste) {
                openUtilitiesModal(updatedRow);
            }
        } catch (e) {
            console.error('Failed to update category', e);
            alert('Failed to update category.');
        } finally {
            setOpenDropdownId(null);
        }
    };

    const openHomeModal = (row, uiCategory) => {
        // Populate structures under this property
        try {
            const options = [];
            // Add property itself
            if (resiProperty && resiProperty.id) {
                const addr = resiProperty?.address?.street_address || resiProperty?.address || '';
                const label = addr ? `Residential Property - ${addr}` : `Residential Property - ${resiProperty.id}`;
                options.push({ id: resiProperty.id, label });
            }
            // Add structures
            const structs = Array.isArray(resiProperty?.structures) ? resiProperty.structures : [];
            for (let idx = 0; idx < structs.length; idx++) {
                const s = structs[idx];
                if (!s || !s.id) continue;
                const sLabel = `${resiProperty?.address?.street_address || ''} - ${(s.description||'Structure')} - Structure ${idx+1}`;
                options.push({ id: s.id, label: sLabel });
            }
            // Add land if available (single or multiple)
            const land = resiProperty?.land;
            if (land) {
                if (Array.isArray(land)) {
                    land.forEach((l, i) => {
                        if (l && l.id) {
                            const lLabel = `${resiProperty?.address?.street_address || ''} - Land ${i+1}`;
                            options.push({ id: l.id, label: lLabel });
                        }
                    });
                } else if (land.id) {
                    const lLabel = `${resiProperty?.address?.street_address || ''} - Land`;
                    options.push({ id: land.id, label: lLabel });
                }
            }
            setHomeAssetOptions(options);
        } catch (e) {
            setHomeAssetOptions([]);
        }
        setHomeRow(row);
        setHomeUiCategory(uiCategory);
        // If already linked to a structure, pre-select it
        setHomeSelectedAssetId(String(row.linked_asset_id || ''));
        // Prefill recurring if known from local meta
        const key = `${row.sourceType}-${row.id}`;
        const meta = linkedMeta[key];
        if (meta && (meta.recurring || meta.frequency)) {
            setHomeIsRecurring(!!meta.recurring);
            setHomeFrequency(meta.frequency || 'monthly');
        } else {
            setHomeIsRecurring(false);
            setHomeFrequency('monthly');
        }
        setHomeExistingSeriesName('');
        setHomeRecurringName('');
        setHomeRecurringType('variable');
        setHomeRecurringMode('existing');
        setHomeError('');
        setShowHomeModal(true);
    };

    // Load recurring series for Home modal
    // - If no specific Structure/Land selected, show all series across Property + children (include_children=1)
    // - If a specific Structure/Land is selected, show only that asset's series
    useEffect(() => {
        const run = async () => {
            try {
                // Helper to label which asset a series belongs to
                const labelForAssetId = (aid) => {
                    try {
                        if (String(aid) === String(resiProperty?.id)) {
                            return 'Property';
                        }
                        const sIdx = (resiProperty?.structures || []).findIndex(s => String(s.id) === String(aid));
                        if (sIdx >= 0) {
                            const sObj = (resiProperty?.structures || [])[sIdx] || {};
                            const sName = sObj.description || 'Structure';
                            return `${sName} (Structure ${sIdx + 1})`;
                        }
                        if (resiProperty?.land?.id && String(resiProperty.land.id) === String(aid)) {
                            return 'Land';
                        }
                        return `Asset ${aid}`;
                    } catch (e) {
                        return `Asset ${aid}`;
                    }
                };

                if (!homeSelectedAssetId) {
                    // No structure selected → query property with include_children=1
                    if (!resiProperty?.id) { setHomeSeriesOptions([]); setHomeExistingSeriesName(''); return; }
                    const url = `/api/balance_sheet/assets/physical_assets/${resiProperty.id}/recurring-series/?include_children=1`;
                    const res = await axios.get(url);
                    const items = Array.isArray(res.data) ? res.data : [];
                    // Enrich with asset labels for clarity in the dropdown
                    const enriched = items.map(s => ({ ...s, _asset_label: labelForAssetId(s.asset_id) }));
                    setHomeSeriesOptions(enriched);
                    setHomeExistingSeriesName('');
                    return;
                }
                // Structure/Land explicitly selected → query that asset only
                const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${homeSelectedAssetId}/recurring-series/`);
                const items = Array.isArray(res.data) ? res.data : [];
                const enriched = items.map(s => ({ ...s, _asset_label: labelForAssetId(homeSelectedAssetId) }));
                setHomeSeriesOptions(enriched);
            } catch (e) {
                setHomeSeriesOptions([]);
            }
        };
        run();
    }, [homeSelectedAssetId, resiProperty?.id]);

    const submitHomeLink = async () => {
        if (!homeRow || !homeSelectedAssetId || !homeUiCategory) { setHomeError('Please select an asset.'); return; }
        setHomeLoading(true);
        setHomeError('');
        try {
            // Validate recurring selection
            if (homeIsRecurring) {
                if (homeRecurringMode === 'existing') {
                    if (!homeExistingSeriesName) { setHomeLoading(false); setHomeError('Please select an existing recurring series.'); return; }
                } else {
                    const name = (homeRecurringName || '').trim();
                    if (!name) { setHomeLoading(false); setHomeError('Please enter a name for the new recurring series.'); return; }
                    const dup = (homeSeriesOptions || []).some(s => String(s.name).toLowerCase() === name.toLowerCase());
                    if (dup) { setHomeLoading(false); setHomeError('A recurring series with this name already exists for this structure.'); return; }
                }
            }
            const url = homeRow.sourceType === 'revolver'
                ? `/api/balance_sheet/financing/revolvers/transactions/${homeRow.id}/link-asset-expense/`
                : `/api/balance_sheet/assets/cash_accounts/transactions/${homeRow.id}/link-asset-expense/`;
            const assetCat = HOME_UI_TO_ASSET[homeUiCategory] || 'maintenance';
            await axios.post(url, {
                asset_id: Number(homeSelectedAssetId),
                expense_category: assetCat,
                description: homeRow.description,
                recurring: homeIsRecurring,
                recurring_frequency: homeIsRecurring && homeRecurringMode === 'new' ? homeFrequency : null,
                recurring_name: homeIsRecurring ? (homeRecurringMode === 'existing' ? homeExistingSeriesName : (homeRecurringName || null)) : null,
                recurring_type: homeIsRecurring ? (homeRecurringMode === 'existing' ? (homeSeriesOptions.find(s => s.name === homeExistingSeriesName)?.type || null) : (homeRecurringType || null)) : null,
            });
            setShowHomeModal(false);
            setHomeRow(null);
            // Optimistic update for linkage
            if (homeRow.sourceType === 'revolver') {
                setRvTxns(prev => prev.map(t => (t.id === homeRow.id ? { ...t, linked_asset_id: Number(homeSelectedAssetId) } : t)));
            } else {
                setCaTxns(prev => prev.map(t => (t.id === homeRow.id ? { ...t, linked_asset_id: Number(homeSelectedAssetId) } : t)));
            }
            const key = `${homeRow.sourceType}-${homeRow.id}`;
            setLinkedMeta(prev => ({ ...prev, [key]: { recurring: !!homeIsRecurring, frequency: homeIsRecurring ? (homeFrequency || '') : '' } }));
        } catch (e) {
            setHomeError('Failed to link home expense.');
        } finally {
            setHomeLoading(false);
        }
    };

    const openLinkModalForRow = (row) => {
        // If this row is a Utilities or Tax expense, open the Utilities variant of the shared modal
        try {
            const cat = String(row?.expense_category || '');
            const isTaxes = cat === 'taxes' || cat === 'home_tax_payment';
            if (isTaxes || (utilitiesChildSet && utilitiesChildSet.has(cat))) {
                const r2 = {
                    id: row.id,
                    sourceType: row.sourceType,
                    description: row.description,
                    amount: row.amount,
                    transaction_date: row.transaction_date,
                    post_date: row.post_date,
                    date: row.transaction_date || row.post_date || '',
                    linked_asset_id: row.linked_asset_id || '',
                    linked_asset_expense_id: row.linked_asset_expense_id || '',
                    linked_utility_bill_id: row.linked_utility_bill_id || '',
                    linked_recurring_series_id: row.linked_recurring_series_id || '',
                    // pass current category so the modal preselects correctly
                    expense_category: cat,
                };
                setUtilityRow(r2);
                setShowUtilityLinkModal(true);
                return;
            }
        } catch (e) { /* fall back to generic modal */ }
        setLinkRow(row);
        setSelectedAssetId(String(row.linked_asset_id || ''));
        // Prepopulate recurring controls from known metadata (if available)
        try {
            const key = `${row.sourceType}-${row.id}`;
            const meta = linkedMeta[key] || {};
            setIsRecurring(!!meta.recurring);
            setRecurringFrequency(meta.frequency || 'monthly');
        } catch (e) {
            setIsRecurring(false);
            setRecurringFrequency('monthly');
        }
        setRecurringSeriesName('');
        setLinkError('');
        setShowLinkModal(true);
    };

    const confirmLink = async () => {
        if (!linkRow || !selectedAssetId) { setLinkError('Please choose Structure or Land.'); return; }
        setLinkLoading(true);
        setLinkError('');
        try {
            const url = linkRow.sourceType === 'revolver'
                ? `/api/balance_sheet/financing/revolvers/transactions/${linkRow.id}/link-asset-expense/`
                : `/api/balance_sheet/assets/cash_accounts/transactions/${linkRow.id}/link-asset-expense/`;
            const body = {
                asset_id: Number(selectedAssetId),
                expense_category: linkRow.expense_category || 'none',
                recurring: !!isRecurring,
                recurring_frequency: isRecurring ? recurringFrequency : null,
                description: linkRow.description,
            };
            await axios.post(url, body);
            // update local transaction linkage
            if (linkRow.sourceType === 'revolver') {
                setRvTxns(prev => prev.map(t => (t.id === linkRow.id ? { ...t, linked_asset_id: Number(selectedAssetId) } : t)));
            } else {
                setCaTxns(prev => prev.map(t => (t.id === linkRow.id ? { ...t, linked_asset_id: Number(selectedAssetId) } : t)));
            }
            const key = `${linkRow.sourceType}-${linkRow.id}`;
            setLinkedMeta(prev => ({ ...prev, [key]: { recurring: !!isRecurring, frequency: isRecurring ? recurringFrequency : '' } }));
            setShowLinkModal(false);
            setLinkRow(null);
        } catch (e) {
            console.error('Failed to link to asset expense', e);
            setLinkError('Failed to link.');
        } finally {
            setLinkLoading(false);
        }
    };

    const deleteTransaction = async (t) => {
        if (!t || !t.id || t.sourceType === 'expected') return; // cannot delete expected rows
        const key = `${t.sourceType}-${t.id}`;
        if (!window.confirm('Are you sure you want to delete this transaction? This cannot be undone.')) return;
        setDeleting(prev => new Set([...prev, key]));
        try {
            if (t.sourceType === 'revolver') {
                await axios.delete(`/api/balance_sheet/financing/revolvers/transactions/${t.id}/`);
                setRvTxns(prev => prev.filter(x => x.id !== t.id));
            } else {
                await axios.delete(`/api/balance_sheet/assets/cash_accounts/transactions/${t.id}/`);
                setCaTxns(prev => prev.filter(x => x.id !== t.id));
            }
        } catch (e) {
            console.error('Failed to delete transaction', e);
            alert('Failed to delete transaction.');
        } finally {
            setDeleting(prev => { const ns = new Set(prev); ns.delete(key); return ns; });
        }
    };

    const renderTransactionsTable = () => {
        return (
            <div style={{ marginTop: 16 }}>
                <div style={{ display:'flex', alignItems:'center', marginBottom:8 }}>
                    <div style={{ fontWeight:600 }}>Transactions in view</div>
                    <button 
                        onClick={handleExport} 
                        className="btn btn-outline-success btn-sm"
                        style={{ marginLeft: 12 }}
                        title="Export to Excel"
                    >
                        <ExcelIcon /> Export
                    </button>
                    <div style={{ marginLeft:'auto', fontSize:12, color:'#6b7280' }}>
                        {txFrom && txTo ? `${txFrom} to ${txTo}` : 'Select a period'}
                    </div>
                </div>
                {txLoading ? (
                    <div style={{ color:'#9ca3af' }}>Loading transactions…</div>
                ) : txError ? (
                    <div className="alert alert-warning" style={{ background:'transparent', color:'#fbbf24' }}>{txError}</div>
                ) : (
                    <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, overflow:'hidden' }}>
                        <table className="table" style={{ margin:0, color:'#e5e7eb', background:'#0b1220', '--bs-table-bg':'#0b1220', '--bs-table-color':'#e5e7eb', '--bs-table-striped-bg':'#0f172a', '--bs-table-accent-bg':'transparent' }}>
                            <thead style={{ background:'#111827', color:'#e5e7eb' }}>
                                <tr>
                                    <th style={{ whiteSpace:'nowrap', cursor:'pointer' }} onClick={()=>handleSort('date')}>Date {sortField==='date' ? (sortDirection==='asc'?'▲':'▼') : ''}</th>
                                    <th style={{ cursor:'pointer' }} onClick={()=>handleSort('group')}>Group {sortField==='group' ? (sortDirection==='asc'?'▲':'▼') : ''}</th>
                                    <th style={{ cursor:'pointer' }} onClick={()=>handleSort('description')}>Description {sortField==='description' ? (sortDirection==='asc'?'▲':'▼') : ''}</th>
                                    <th style={{ cursor:'pointer' }} onClick={()=>handleSort('user_description')}>User Description {sortField==='user_description' ? (sortDirection==='asc'?'▲':'▼') : ''}</th>
                                    <th style={{ minWidth:'180px', cursor:'pointer' }} onClick={()=>handleSort('category')}>Expense Category {sortField==='category' ? (sortDirection==='asc'?'▲':'▼') : ''}</th>
                                    <th style={{ textAlign:'right', cursor:'pointer' }} onClick={()=>handleSort('amount')}>Amount {sortField==='amount' ? (sortDirection==='asc'?'▲':'▼') : ''}</th>
                                    <th style={{ cursor:'pointer' }} onClick={()=>handleSort('source')}>Source {sortField==='source' ? (sortDirection==='asc'?'▲':'▼') : ''}</th>
                                    <th style={{ cursor:'pointer' }} onClick={()=>handleSort('recurring_name')}>Recurring Expense {sortField==='recurring_name' ? (sortDirection==='asc'?'▲':'▼') : ''}</th>
                                    <th style={{ whiteSpace:'nowrap' }}>Asset Link</th>
                                    <th style={{ whiteSpace:'nowrap' }}>Linked To-Do</th>
                                    <th style={{ whiteSpace:'nowrap' }}>Debt Link</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody style={{ background:'#0b1220' }}>
                                {(() => {
                                    // Start from filtered transactions for the selected assets/date range
                                    const rows = [...[...filteredTxns]];
                                    // Remove real cash child transactions from the top-level set; they will be shown under parents
                                    const topLevel = rows.filter(r => !(r && r.sourceType === 'cash' && cashHierarchy.childIds.has(`cash-${r.id}`)));
                                    const keyFor = (t) => `${t.sourceType}-${t.id}`;
                                    const getTime = (t) => {
                                        const s = t.transaction_date || t.post_date;
                                        return s ? new Date(s).getTime() : 0;
                                    };
                                    topLevel.sort((a,b)=>{
                                        const dir = (sortDirection==='asc') ? 1 : -1;
                                        const getRecName = (t) => t.recurring_name || t.linked_expense_info?.recurring_name || '';
                                        const getRecFreq = (t) => t.recurring_frequency || t.linked_expense_info?.recurring_frequency || '';
                                        const getRecType = (t) => t.recurring_type || t.linked_expense_info?.recurring_type || '';

                                        switch (sortField) {
                                            case 'date': return (getTime(a)-getTime(b))*dir;
                                            case 'group': return String(a.expense_group||'').localeCompare(String(b.expense_group||''))*dir;
                                            case 'description': return String(a.description||'').localeCompare(String(b.description||''))*dir;
                                            case 'user_description': return String(a.user_description||'').localeCompare(String(b.user_description||''))*dir;
                                            case 'category': return String(resolveCategoryLabel(a.expense_category||'')).localeCompare(String(resolveCategoryLabel(b.expense_category||'')))*dir;
                                            case 'amount': return ((getDisplayAmount(a))-(getDisplayAmount(b)))*dir;
                                            case 'source': {
                                                const sa = a.sourceType==='cash' ? (a.source_account_display||'Cash') : (a.revolver?.lender?.name||'Credit Card');
                                                const sb = b.sourceType==='cash' ? (b.source_account_display||'Cash') : (b.revolver?.lender?.name||'Credit Card');
                                                return String(sa).localeCompare(String(sb))*dir;
                                            }
                                            case 'structureType': {
                                                const getST = (t)=>{
                                                    if (landId && String(t.linked_asset_id)===String(landId)) return 'Land';
                                                    const idx = structureIds.findIndex(sid=>String(sid)===String(t.linked_asset_id));
                                                    if (idx>=0) {
                                                        const s = (resiProperty.structures||[]).find(st=>String(st.id)===String(t.linked_asset_id))||{};
                                                        return s.description||'Structure';
                                                    }
                                                    return '';
                                                };
                                                return String(getST(a)).localeCompare(String(getST(b)))*dir;
                                            }
                                            case 'structureId': {
                                                const getLabel = (t)=>{
                                                    if (landId && String(t.linked_asset_id)===String(landId)) return 'Land';
                                                    const idx = structureIds.findIndex(sid=>String(sid)===String(t.linked_asset_id));
                                                    return idx>=0?`Structure ${idx+1}`:'';
                                                };
                                                return String(getLabel(a)).localeCompare(String(getLabel(b)))*dir;
                                            }
                                            case 'recurring_name': {
                                                return String(getRecName(a)).localeCompare(String(getRecName(b)))*dir;
                                            }
                                            case 'recurring': {
                                                const ra = (linkedMeta[keyFor(a)]?.recurring || a.recurring || a.linked_expense_info?.recurring) ? 1 : 0;
                                                const rb = (linkedMeta[keyFor(b)]?.recurring || b.recurring || b.linked_expense_info?.recurring) ? 1 : 0;
                                                return (ra-rb)*dir;
                                            }
                                            case 'frequency': {
                                                const fa = getRecFreq(a) || linkedMeta[keyFor(a)]?.frequency || '';
                                                const fb = getRecFreq(b) || linkedMeta[keyFor(b)]?.frequency || '';
                                                return String(fa).localeCompare(String(fb))*dir;
                                            }
                                            default: return (getTime(a)-getTime(b))*dir;
                                        }
                                    });
                                    return topLevel.length>0 ? topLevel.flatMap((t, idx) => {
                                        const cat = t.expense_category || 'none';
                                        const style = getCategoryStyle(cat);
                                        const catLabel = formatParentChildLabel(cat);
                                        let structType = '';
                                        let structIdLabel = '';
                                        if (landId && String(t.linked_asset_id) === String(landId)) {
                                            structType = 'Land';
                                            structIdLabel = 'Land';
                                        } else {
                                            const si = structureIds.findIndex(sid => String(sid) === String(t.linked_asset_id));
                                            if (si >= 0) {
                                                const s = (resiProperty.structures || []).find(st => String(st.id) === String(t.linked_asset_id)) || {};
                                                structType = s.description || 'Structure';
                                                structIdLabel = `Structure ${si + 1}`;
                                            }
                                        }
                                        const key = `${t.sourceType}-${t.id}`;
                                        const darkBg = idx % 2 ? '#0f172a' : '#111827';
                                        const parentRow = (
                                            <tr key={key} style={{ background: darkBg }}>
                                                <td style={{ whiteSpace:'nowrap' }}>{t.transaction_date || t.post_date || ''}</td>
                                                <td><ExpenseGroupPill group={t.expense_group} /></td>
                                                <td>
                                                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                                        {/* Expand/collapse toggle for cash parents with children */}
                                                        {(t.sourceType==='cash' && (t.children_count > 0 || t.has_children)) ? (
                                                            <button
                                                              type="button"
                                                              className="btn btn-sm btn-outline-secondary"
                                                              onClick={() => {
                                                                const pkey = `cash-${t.id}`;
                                                                setExpandedTxns(prev => {
                                                                  const n = new Set([...prev]);
                                                                  if (n.has(pkey)) n.delete(pkey); else n.add(pkey);
                                                                  return n;
                                                                });
                                                              }}
                                                              title={expandedTxns.has(`cash-${t.id}`) ? 'Collapse' : 'Expand'}
                                                              style={{ padding:'2px 6px' }}
                                                            >
                                                              {expandedTxns.has(`cash-${t.id}`) ? '−' : '+'}
                                                            </button>
                                                        ) : (
                                                            <span style={{ width: 26 }}></span>
                                                        )}
                                                        <span>{t.description || ''}</span>
                                                    </div>
                                                </td>
                                                <td>{t.user_description || ''}</td>
                                                <td style={{ minWidth:'220px', whiteSpace:'nowrap' }}>
                                                    {t.sourceType === 'expected' ? (
                                                      <span
                                                        className="btn btn-sm"
                                                        style={{
                                                          backgroundColor: style.backgroundColor,
                                                          color: style.color,
                                                          borderRadius: '12px',
                                                          fontSize: '0.75rem',
                                                          padding: '4px 8px',
                                                          border: `1px solid ${style.borderColor}`,
                                                          cursor: 'default'
                                                        }}
                                                        title="Expected transactions have fixed categories"
                                                      >
                                                        {catLabel}
                                                      </span>
                                                    ) : (
                                                      <div className="position-relative" style={{ display: 'inline-block' }}>
                                                        <button
                                                          className="btn btn-sm"
                                                          type="button"
                                                          onClick={(e) => toggleDropdown(e, key)}
                                                          style={{
                                                            backgroundColor: style.backgroundColor,
                                                            color: style.color,
                                                            borderRadius: '12px',
                                                            fontSize: '0.75rem',
                                                            padding: '4px 8px',
                                                            cursor: 'pointer',
                                                            border: `1px solid ${style.borderColor}`
                                                          }}
                                                        >
                                                          {catLabel} &nbsp;▼
                                                        </button>
                                                        {openDropdownId === key && (
                                                          <div
                                                            className="position-absolute"
                                                            style={{ top: '100%', left: 0, zIndex: 1000, minWidth: '320px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.375rem', boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.3)', marginTop: 2 }}
                                                            onClick={(e) => e.stopPropagation()}
                                                          >
                                                            <div style={{ maxHeight: '480px', overflowY: 'auto', padding: '4px 0' }}>
                                                              {expenseCategories.items.map(it => (
                                                                <div key={it.value}>
                                                                  <button
                                                                    className="w-100 text-start"
                                                                    style={{ display: 'block', padding: '8px 16px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                                                    onClick={() => {
                                                                      if (Array.isArray(it.children) && it.children.length > 0) {
                                                                        toggleParentExpand(key, it.value);
                                                                      } else {
                                                                        updateExpenseCategory(t, String(it.value));
                                                                      }
                                                                    }}
                                                                  >
                                                                    {(Array.isArray(it.children) && it.children.length > 0) ? (isParentExpanded(key, it.value) ? '▼ ' : '► ') : ''}
                                                                    {it.label}
                                                                  </button>
                                                                  {(Array.isArray(it.children) && it.children.length > 0 && isParentExpanded(key, it.value)) && (
                                                                    <div>
                                                                      {it.children.map(ch => (
                                                                        <button key={ch.value}
                                                                          className="w-100 text-start"
                                                                          style={{ display:'block', padding:'6px 24px', border:'none', backgroundColor:'#0f172a', color:'#e5e7eb', cursor:'pointer', fontSize:'0.85rem' }}
                                                                          onMouseEnter={(e)=> e.currentTarget.style.backgroundColor = '#1f2937'}
                                                                          onMouseLeave={(e)=> e.currentTarget.style.backgroundColor = '#0f172a'}
                                                                          onClick={()=> updateExpenseCategory(t, String(ch.value))}
                                                                        >
                                                                          {ch.label}
                                                                        </button>
                                                                      ))}
                                                                    </div>
                                                                  )}
                                                                </div>
                                                              ))}
                                                              <div>
                                                                <button
                                                                  className="w-100 text-start"
                                                                  style={{ display:'block', padding:'8px 16px', border:'none', backgroundColor:'#111827', color:'#e5e7eb', cursor:'pointer' }}
                                                                  onMouseEnter={(e)=> e.currentTarget.style.backgroundColor = '#1f2937'}
                                                                  onMouseLeave={(e)=> e.currentTarget.style.backgroundColor = '#111827'}
                                                                  onClick={()=> updateExpenseCategory(t, String(expenseCategories.noneItem?.value || 'none'))}
                                                                >
                                                                  {expenseCategories.noneItem?.label || 'None'}
                                                                </button>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                </td>
                                                <td style={{ textAlign:'right' }}>{formatCurrency(getDisplayAmount(t) || 0)}</td>
                                                <td style={{ whiteSpace:'nowrap' }}>{t.sourceType === 'cash' ? (t.source_account_display || 'Cash') : (t.revolver?.lender?.name || 'Credit Card')}</td>
                                                <td>
                                                    {(() => {
                                                        const recName = t.recurring_name || t.linked_expense_info?.recurring_name;
                                                        const recFreq = t.recurring_frequency || t.linked_expense_info?.recurring_frequency;
                                                        const recType = t.recurring_type || t.linked_expense_info?.recurring_type;
                                                        if (!recName) return null;
                                                        return (
                                                            <button
                                                                className="btn btn-link btn-sm"
                                                                style={{ color:'#93c5fd', padding:0, textAlign:'left', textDecoration:'none' }}
                                                                title={`Frequency: ${recFreq || 'N/A'}\nType: ${recType || 'N/A'}`}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    setSelectedRecurringName(recName);
                                                                    setChartTypeWithScroll('expenses');
                                                                    setExpensesSubView('recurring');
                                                                }}
                                                            >
                                                                {recName}
                                                            </button>
                                                        );
                                                    })()}
                                                </td>
                                                <td>
                                                    <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                                                        {(() => {
                                                            if (t.sourceType === 'expected') return null;
                                                            if (!t.linked_asset_id) {
                                                                const cat = String(t.expense_category || '');
                                                                const domain = getDomainForCategory(cat);
                                                                const isWaste = cat.startsWith('waste_') || cat.includes('sanitation');
                                                                const isLinkable = domain !== 'generic' || isWaste;
                                                                if (!isLinkable) return null;

                                                                let labelBtn = 'Link Asset';
                                                                if (domain === 'utilities') labelBtn = 'Link Utilities';
                                                                else if (domain === 'home') labelBtn = 'Link Home';
                                                                else if (domain === 'landscaping') labelBtn = 'Link Landscaping';
                                                                else if (domain === 'vehicle') labelBtn = 'Link Vehicle';
                                                                else if (isWaste) labelBtn = 'Link Waste';

                                                                return (
                                                                    <button
                                                                        className="btn btn-link btn-sm"
                                                                        style={{ color:'#93c5fd', padding:0 }}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            openUtilitiesModal(t);
                                                                        }}
                                                                    >
                                                                        {labelBtn}
                                                                    </button>
                                                                );
                                                            }
                                                            const asset = assetMaps.byId.get(String(t.linked_asset_id));
                                                            const url = buildAssetUrl(asset);
                                                            const cat = String(t.expense_category || '');
                                                            const domain = getDomainForCategory(cat);
                                                            const label = buildAssetLabel(asset, { ...assetMaps, transactionDomain: domain });

                                                            return (
                                                                <>
                                                                    {url ? (
                                                                        <Link to={url} title={label} style={{ color:'#93c5fd', whiteSpace:'nowrap', background:'transparent', border:'none' }}>{label}</Link>
                                                                    ) : (
                                                                        <span title={label} style={{ color:'#93c5fd' }}>{label}</span>
                                                                    )}
                                                                    <button
                                                                        className="btn btn-link btn-sm"
                                                                        style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            openUtilitiesModal(t);
                                                                        }}
                                                                    >
                                                                        Edit Link
                                                                    </button>
                                                                </>
                                                            );
                                                        })()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                                                        {(() => {
                                                            const todoInfo = t.linked_todo_info;
                                                            if (!todoInfo) return null;

                                                            let url = null;
                                                            if (todoInfo.asset_type === 'vehicle') {
                                                                url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                                                            } else if (todoInfo.property_id) {
                                                                url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                                                            }

                                                            const statusColor = todoInfo.status === 'completed' ? '#22c55e' : (todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af');
                                                            
                                                            const content = (
                                                                <span 
                                                                    style={{ color: '#93c5fd', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.85rem' }}
                                                                    title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}
                                                                >
                                                                    {todoInfo.title}
                                                                </span>
                                                            );

                                                            return (
                                                                <>
                                                                    {url ? (
                                                                        <Link to={url}>{content}</Link>
                                                                    ) : (
                                                                        content
                                                                    )}
                                                                    <span style={{ fontSize: '10px', color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '4px', padding: '0 4px', marginLeft: '4px' }}>
                                                                        {todoInfo.status_display}
                                                                    </span>
                                                                </>
                                                            );
                                                        })()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                                                        {t.sourceType === 'cash' && t.linked_debt_info && (t.linked_debt_info.type === 'auto_loan' || t.linked_debt_info.type === 'mortgage') && t.linked_debt_id ? (
                                                            <>
                                                                <a
                                                                    href={t.linked_debt_info.type === 'auto_loan' ? `/balance_sheet/financing/auto_loans/${t.linked_debt_id}` : `/balance_sheet/financing/mortgages/${t.linked_debt_id}`}
                                                                    style={{ color:'#93c5fd', whiteSpace:'nowrap', background:'transparent', border:'none' }}
                                                                    title={t.linked_debt_info.display_text || ''}
                                                                >
                                                                    {t.linked_debt_info.debt_label || t.linked_debt_info.debt_name || '🔗 Debt'}
                                                                </a>
                                                                <button
                                                                    className="btn btn-link btn-sm"
                                                                    style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        openDebtModalForRow(t);
                                                                    }}
                                                                >
                                                                    Edit Link
                                                                </button>
                                                            </>
                                                        ) : (
                                                            t.sourceType === 'cash' && String(t.expense_category || '') === 'payments' && (
                                                                <button
                                                                    className="btn btn-link btn-sm"
                                                                    style={{ color:'#93c5fd', padding:0 }}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        openDebtModalForRow(t);
                                                                    }}
                                                                >
                                                                    Link Debt
                                                                </button>
                                                            )
                                                        )}
                                                    </span>
                                                </td>
                                                <td>
                                                  {t.sourceType === 'expected' ? null : (
                                                    <button
                                                      className="btn btn-danger btn-sm"
                                                      onClick={() => deleteTransaction(t)}
                                                      disabled={deleting.has(`${t.sourceType}-${t.id}`)}
                                                      style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                                    >
                                                      {deleting.has(`${t.sourceType}-${t.id}`) ? 'Deleting...' : 'Delete'}
                                                    </button>
                                                  )}
                                                </td>
                                            </tr>
                                        );

                                        const rowsOut = [parentRow];
                                        // Render children when expanded
                                        const expKey = `cash-${t.id}`;
                                        if (t.sourceType==='cash' && expandedTxns.has(expKey)) {
                                            const realKids = cashHierarchy.childrenByParent.get(expKey) || [];
                                            if (realKids.length > 0) {
                                                for (const kt of realKids) {
                                                    const kidKey = `cash-child-${kt.id}`;
                                                    rowsOut.push(
                                                      <tr key={kidKey} style={{ background:'#0b1220' }}>
                                                        <td style={{ whiteSpace:'nowrap' }}>{kt.transaction_date || kt.post_date || ''}</td>
                                                        <td><ExpenseGroupPill group={kt.expense_group} /></td>
                                                        <td>
                                                          <div style={{ paddingLeft: 16 }}>{kt.description || ''}</div>
                                                        </td>
                                                        <td>{kt.user_description || ''}</td>
                                                        <td>
                                                          <span className="badge" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151', borderRadius:'12px', padding:'4px 8px' }}>{formatParentChildLabel(kt.expense_category || 'none')}</span>
                                                        </td>
                                                        <td style={{ textAlign:'right' }}>{formatCurrency(getDisplayAmount({ ...kt, sourceType:'cash_child' }) || 0)}</td>
                                                        <td>{t.source_account_display || 'Cash'}</td>
                                                        <td>
                                                            {(() => {
                                                                const recName = kt.recurring_name || kt.linked_expense_info?.recurring_name;
                                                                const recFreq = kt.recurring_frequency || kt.linked_expense_info?.recurring_frequency;
                                                                const recType = kt.recurring_type || kt.linked_expense_info?.recurring_type;
                                                                if (!recName) return null;
                                                                return (
                                                                    <button
                                                                        className="btn btn-link btn-sm"
                                                                        style={{ color:'#93c5fd', padding:0, textAlign:'left', textDecoration:'none' }}
                                                                        title={`Frequency: ${recFreq || 'N/A'}\nType: ${recType || 'N/A'}`}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            setSelectedRecurringName(recName);
                                                                            setChartTypeWithScroll('expenses');
                                                                            setExpensesSubView('recurring');
                                                                        }}
                                                                    >
                                                                        {recName}
                                                                    </button>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const asset = assetMaps.byId.get(String(kt.linked_asset_id));
                                                                if (!asset) return null;
                                                                const label = buildAssetLabel(asset, { ...assetMaps, transactionDomain: getDomainForCategory(kt.expense_category) });
                                                                return <span style={{ color:'#9ca3af' }}>{label}</span>;
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const todoInfo = kt.linked_todo_info;
                                                                if (!todoInfo) return null;

                                                                let url = null;
                                                                if (todoInfo.asset_type === 'vehicle') {
                                                                    url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                                                                } else if (todoInfo.property_id) {
                                                                    url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                                                                }

                                                                const statusColor = todoInfo.status === 'completed' ? '#22c55e' : (todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af');
                                                                
                                                                const content = (
                                                                    <span 
                                                                        style={{ color: '#9ca3af', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.8rem' }}
                                                                        title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}
                                                                    >
                                                                        {todoInfo.title}
                                                                    </span>
                                                                );

                                                                return (
                                                                    <>
                                                                        {url ? (
                                                                            <Link to={url}>{content}</Link>
                                                                        ) : (
                                                                            content
                                                                        )}
                                                                        <span style={{ fontSize: '9px', color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '4px', padding: '0 3px', marginLeft: '3px' }}>
                                                                            {todoInfo.status_display}
                                                                        </span>
                                                                    </>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td></td>
                                                        <td></td>
                                                      </tr>
                                                    );
                                                }
                                            } else if (Array.isArray(t.children) && t.children.length > 0) {
                                                // Synthesized children from backend (loan split components)
                                                for (const sc of t.children) {
                                                    const kidKey = `cash-synth-${t.id}-${sc.id}`;
                                                    const label = sc.category_label || sc.expense_category || 'None';
                                                    rowsOut.push(
                                                      <tr key={kidKey} style={{ background:'#0b1220' }}>
                                                        <td style={{ whiteSpace:'nowrap' }}>{sc.transaction_date || sc.post_date || ''}</td>
                                                        <td><ExpenseGroupPill group={sc.expense_group} /></td>
                                                        <td>
                                                          <div style={{ paddingLeft: 16 }}>{sc.description || 'Loan payment component'}</div>
                                                        </td>
                                                        <td>{sc.user_description || ''}</td>
                                                        <td>
                                                          <span className="badge" title={sc.read_only_category ? 'This split category is read-only' : ''} style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151', borderRadius:'12px', padding:'4px 8px' }}>{label}</span>
                                                        </td>
                                                        <td style={{ textAlign:'right' }}>{formatCurrency(getDisplayAmount({ amount: sc.amount, sourceType:'cash_child' }) || 0)}</td>
                                                        <td>{t.source_account_display || 'Cash'}</td>
                                                        <td>
                                                            {(() => {
                                                                const recName = sc.recurring_name || sc.linked_expense_info?.recurring_name;
                                                                const recFreq = sc.recurring_frequency || sc.linked_expense_info?.recurring_frequency;
                                                                const recType = sc.recurring_type || sc.linked_expense_info?.recurring_type;
                                                                if (!recName) return null;
                                                                return (
                                                                    <button
                                                                        className="btn btn-link btn-sm"
                                                                        style={{ color:'#93c5fd', padding:0, textAlign:'left', textDecoration:'none' }}
                                                                        title={`Frequency: ${recFreq || 'N/A'}\nType: ${recType || 'N/A'}`}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            setSelectedRecurringName(recName);
                                                                            setChartTypeWithScroll('expenses');
                                                                            setExpensesSubView('recurring');
                                                                        }}
                                                                    >
                                                                        {recName}
                                                                    </button>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const asset = assetMaps.byId.get(String(sc.linked_asset_id || t.linked_asset_id));
                                                                if (!asset) return null;
                                                                const label = buildAssetLabel(asset, { ...assetMaps, transactionDomain: getDomainForCategory(sc.expense_category) });
                                                                return <span style={{ color:'#9ca3af' }}>{label}</span>;
                                                            })()}
                                                        </td>
                                                        <td></td>
                                                        <td></td>
                                                      </tr>
                                                    );
                                                }
                                            }
                                        }

                                        return rowsOut;
                                    }) : (
                                    <tr style={{ background:'#111827' }}>
                                            <td colSpan={11} className="text-center text-muted" style={{ color:'#9ca3af' }}>No transactions for the selected period.</td>
                                        </tr>
                                    );
                                })()}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    // Build hierarchy for cash transactions (parents and real cash children)
    const [expandedTxns, setExpandedTxns] = useState(new Set());
    const cashHierarchy = React.useMemo(() => {
        const byId = new Map();
        const childrenByParent = new Map();
        const childIds = new Set();
        // Use the full cash list to construct, not just filteredTxns
        for (const t of (caTxns || [])) {
            byId.set(`cash-${t.id}`, t);
        }
        for (const t of (caTxns || [])) {
            const p = t.parent;
            if (p) {
                const key = `cash-${p}`;
                if (!childrenByParent.has(key)) childrenByParent.set(key, []);
                childrenByParent.get(key).push(t);
                childIds.add(`cash-${t.id}`);
            }
        }
        for (const [k, arr] of childrenByParent.entries()) {
            arr.sort((a,b)=>{
                const ad = a.post_date || a.transaction_date; const bd = b.post_date || b.transaction_date;
                if (ad === bd) return a.id - b.id; return (ad < bd) ? -1 : 1;
            });
        }
        return { byId, childrenByParent, childIds };
    }, [caTxns]);

    if (loading) return <div>Loading...</div>;
    if (!resiProperty) return <div>No residential property found!</div>;

    const handleAddFinancing = () => {
        navigate('/financing/create-loan');  // Redirect to create loan form
    };

    const handleLinkMortgage = async () => {
        if (selectedMortgageId) {
            try {
                await axios.patch(`/api/balance_sheet/assets/physical_assets/resi_properties/${id}/`, {
                    financing: selectedMortgageId  // Pass the selected mortgage ID
                });
                // Optionally, update state to reflect the change
                setResiProperty(prev => ({...prev, financing_source: selectedMortgageId}));
            } catch (error) {
                console.error('Error linking mortgage:', error);
            }
        }
    };

    const handleLandSubmit = async (landData) => {
        console.log('landData before post:', landData)
        try {
            const response = await axios.post(`/api/balance_sheet/assets/physical_assets/resi_properties/${resiProperty.id}/add-land/`, landData);
            console.log('Land created:', response.data);
            setLandFormVisible(false); // Hide land form after submission if desired

            // Re-fetch the residential property data including the new land
            const updatedResponse = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${resiProperty.id}/`);
            setResiProperty(updatedResponse.data); // Update the state with the newly fetched data

        } catch (error) {
            console.error('Error creating land:', error);
            setError('There was an error creating the land. Please check your input.');
        }
    };

    const handleStructureSubmit = async (structureData) => {

        try {
            const response = await axios.post(`/api/balance_sheet/assets/physical_assets/resi_properties/${resiProperty.id}/create-structure/`, structureData);
            console.log('Structure created:', response.data);
            setStructureFormVisible(false); // Hide structure form after submission if desired

            // Re-fetch the residential property data including the new land
            const updatedResponse = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${resiProperty.id}/`);
            setResiProperty(updatedResponse.data); // Update the state with the newly fetched data

        } catch (error) {
            console.error('Error creating structure:', error);
            setError('There was an error creating the structure. Please check your input.');
        }
    };

    return (
        <div>
            <div style={{margin: '20px', marginBottom: '12px'}}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <h1 style={{marginRight: '20px'}}>Residential Property Details</h1>
                    <div>
                        <Link style={{marginRight: '10px'}}
                              to="/balance_sheet/assets/physical_assets/resi_properties/"> Residential
                            Properties</Link> |
                        <Link style={{marginRight: '10px'}} to="/balance_sheet/assets/physical_assets/"> Physical
                            Assets</Link> |
                        <Link to="/assets/"> All Assets</Link>
                    </div>
                    {/* Removed weather from top header; it will be shown next to the address title */}
                </div>
                {/* Address row with weather near the title (left) and action buttons on the right */}
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px'}}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <h2 style={{marginRight: '12px', marginBottom: 0}}>
                            {resiProperty?.address?.street_address}, {resiProperty?.address?.city}, {resiProperty?.address?.state} {resiProperty?.address?.postal_code}
                        </h2>
                        {/* Weather icons inline next to the address title */}
                        <div>
                            {resiProperty?.id ? (
                                <WeatherForecastIcons propertyId={resiProperty.id} />
                            ) : null}
                        </div>
                    </div>
                    {/* Action buttons aligned to the right */}
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    </div>
                    <Modal isOpen={showValuationForm} onClose={closeValuationModal} title="Add Valuation">
                        {currentAssetId && (
                            <ValuationForm assetId={currentAssetId} onClose={closeValuationModal} />
                        )}
                    </Modal>

                    <ValuationsModal
                        isOpen={showEditValuationsModal}
                        onClose={() => setShowEditValuationsModal(false)}
                        assetId={currentAssetId}
                        onValuationsUpdated={() => setChartsRefresh(v => v + 1)}
                    />
                </div>
                {/* Tabs header: View tabs on the left, asset-scope selectors on the right (moved to top) */}
                <div style={{ margin: '0 20px 8px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Left: View tabs */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flexWrap: 'wrap' }}>
                    {[
                      { id: 'summary', label: 'Summary' },
                      { id: 'expenses', label: 'Expenses' },
                      { id: 'utilities', label: 'Utilities' },
                      { id: 'debt_equity', label: 'Debt & Equity' },
                      { id: 'book_market', label: 'Valuations' },
                      { id: 'tax', label: 'Tax Assessments' },
                      { id: 'todo', label: 'To-Do List' },
                    ].map(v => {
                      const isActive = chartType === v.id;
                      return (
                        <span
                          key={v.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => { setChartTypeWithScroll(v.id); }}
                          onKeyDown={(e)=>{
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setChartTypeWithScroll(v.id);
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            padding: '6px 10px',
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? '#2563eb' : '#6b7280',
                            border: `1px solid ${isActive ? '#2563eb' : '#d1d5db'}`,
                            borderBottom: isActive ? '1px solid transparent' : `1px solid #d1d5db`,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            background: isActive ? '#0b1220' : 'transparent',
                            position: 'relative',
                            top: 1,
                            transition: 'color 120ms ease, border-color 120ms ease, background-color 120ms ease',
                            outline: 'none'
                          }}
                        >
                          {v.label}
                        </span>
                      );
                    })}
                  </div>
                  {/* Right: Property/Structures/Land text-only selectors (+ optional Add Structure) */}
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    {chartType === 'tax' && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => { setEditingAssessment(null); setShowTaxModal(true); }}
                      >
                        Add Assessment
                      </button>
                    )}
                    {chartType === 'book_market' && (
                      <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => openValuationModal(resiProperty.id)}
                      >
                        Add Valuation
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => { setCurrentAssetId(resiProperty.id); setShowEditValuationsModal(true); }}
                      >
                        Edit Valuations
                      </button>
                      </>
                    )}
                    {(chartType === 'utilities') && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowUtilityModal(true)}
                      >
                        Upload Utility Bills
                      </button>
                    )}
                    {['property', 'structures', 'land'].map(tab => {
                      const isActive = activeTab === tab;
                      return (
                        <span
                          key={tab}
                          role="button"
                          tabIndex={0}
                          onClick={() => { setActiveTabWithScroll(tab); }}
                          onKeyDown={(e)=>{
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setActiveTabWithScroll(tab);
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            paddingBottom: 6,
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? '#2563eb' : '#6b7280',
                            borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                            outline: 'none'
                          }}
                        >
                          {tab === 'property' ? 'Property' : tab === 'structures' ? 'Structures' : 'Land'}
                        </span>
                      );
                    })}
                    {activeTab === 'structures' && (
                      <button className="btn btn-sm btn-outline-primary" onClick={() => setStructureFormVisible(true)}>
                        + Add Structure
                      </button>
                    )}
                  </div>
                </div>
            </div>

            {showUtilityReview && (
                          <UtilityBillReviewModal
                            propertyId={id}
                            results={utilityResults}
                            startIndex={utilityReviewStartIndex}
                            onDone={() => { setShowUtilityReview(false); setUtilityResults([]); setUtilityFiles([]); setShowUtilityModal(false); }}
                            onCancel={() => { setShowUtilityReview(false); }}
                          />
                        )}

            {showTaxModal && (
              <TaxAssessmentModal
                isOpen={showTaxModal}
                onClose={() => { setShowTaxModal(false); setEditingAssessment(null); }}
                propertyId={id}
                assessment={editingAssessment}
                onSave={(data) => {
                  fetchTaxAssessments();
                  setChartsRefresh(v => v + 1);
                }}
              />
            )}

                        {showUtilityModal && (
              <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
                <div style={{ width:'min(800px, 95vw)', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
                  <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                    <div style={{ fontSize:18, fontWeight:700 }}>Upload Utility Bills</div>
                    <button onClick={()=>{ setShowUtilityModal(false); setUtilityResults([]); setUtilityFiles([]); setUtilityError(''); }} style={{ marginLeft:'auto' }} className="btn btn-sm btn-secondary">✕</button>
                  </div>
                  <div style={{ padding:16 }}>
                    {utilityError && (<div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{utilityError}</div>)}
                    <div className="row" style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr 2fr', gap:12 }}>
                      <div>
                        <label className="form-label">Utility Provider</label>
                        <select className="form-select" value={utilityProvider} onChange={e=> setUtilityProvider(e.target.value)}>
                          <option value="MidAmerican">MidAmerican</option>
                          <option value="City of Ankeny">City of Ankeny</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Utility Type</label>
                        <select className="form-select" value={utilityType} onChange={e=> setUtilityType(e.target.value)}>
                          <option value="electric_gas">Electric & Gas</option>
                          <option value="electric">Electric</option>
                          <option value="gas">Gas</option>
                          <option value="water">Water</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">PDF Files</label>
                        <input className="form-control" type="file" accept="application/pdf" multiple onChange={e=> setUtilityFiles(Array.from(e.target.files || []))} />
                        <small className="form-text text-muted">Select one or more PDF bills from the same provider.</small>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                      <button className="btn btn-secondary" onClick={()=> { setShowUtilityModal(false); setUtilityResults([]); setUtilityFiles([]); setUtilityError(''); }} disabled={utilityUploading}>Cancel</button>
                      <button className="btn btn-success" onClick={async ()=>{
                        setUtilityError('');
                        setUtilityResults([]);
                        if (!utilityFiles || utilityFiles.length === 0) { setUtilityError('Please select at least one PDF.'); return; }
                          try {
                            setUtilityUploading(true);
                            const form = new FormData();
                            utilityFiles.forEach(f => form.append('files', f));
                            form.append('provider', utilityProvider);
                            form.append('utility_type', utilityType);
                            // Post to backend endpoint
                            const url = `/api/balance_sheet/assets/physical_assets/resi_properties/${id}/upload-utility-bills/`;
                            const res = await axios.post(url, form);
                            const data = res.data;
                            const newResults = Array.isArray(data?.results) ? data.results : [];
                            setUtilityResults(newResults);
                            // Open review modal starting at the first bill
                            if (newResults.length > 0) {
                              // Close the upload modal and open the review form modal
                              setShowUtilityModal(false);
                              setShowUtilityReview(true);
                              setUtilityReviewStartIndex(0);
                            }
                          } catch (e) {
                            setUtilityError(e.response?.data?.detail || e.message || 'An error occurred during upload.');
                          } finally {
                          setUtilityUploading(false);
                        }
                      }} disabled={utilityUploading}>
                        {utilityUploading ? 'Uploading…' : 'Upload & Parse'}
                      </button>
                    </div>

                    {utilityResults && utilityResults.length > 0 && (
                      <div style={{ marginTop:16 }}>
                        <div style={{ fontWeight:600, marginBottom:8 }}>Parsed Results</div>
                        <div style={{ maxHeight: '50vh', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                          {utilityResults.map((r, i) => (
                            <div key={i} style={{ padding:12, borderBottom:'1px solid #374151' }}>
                              <div style={{ marginBottom:6 }}>
                                <strong>File:</strong> {r.filename || `#${i+1}`} &nbsp; | &nbsp; <strong>Provider:</strong> {r.provider}
                              </div>
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                                <div>
                                  <div style={{ fontWeight:600, marginBottom:4 }}>Raw Text</div>
                                  <pre style={{ whiteSpace:'pre-wrap', color:'#e5e7eb', margin:0, maxHeight: '35vh', overflowY:'auto' }}>{r.raw_text || ''}</pre>
                                </div>
                                <div>
                                  <div style={{ fontWeight:600, marginBottom:4 }}>Parsed (Summary)</div>
                                  <pre style={{ whiteSpace:'pre-wrap', color:'#e5e7eb', margin:0, background:'#0f172a', padding:8, borderRadius:6 }}>
{JSON.stringify(r.parsed || {}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Global Unlink Confirmation Modal */}
            {showUnlinkModal && (
              <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2200 }} onClick={()=> !unlinkLoading && setShowUnlinkModal(false)}>
                <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(520px,95vw)' }} onClick={(e)=> e.stopPropagation()}>
                  <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                    <div style={{ fontSize:18, fontWeight:700 }}>Unlink Transaction</div>
                    <button className="btn btn-sm btn-secondary" onClick={()=> setShowUnlinkModal(false)} style={{ marginLeft:'auto' }} disabled={unlinkLoading}>✕</button>
                  </div>
                  <div style={{ padding:16 }}>
                    {unlinkError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{unlinkError}</div>}
                    <div>Are you sure you want to unlink this transaction from the selected asset? This will remove the asset expense linkage.</div>
                    {unlinkRow && (
                      <div style={{ marginTop:10, fontSize:13, color:'#9ca3af' }}>
                        <div><strong>Date:</strong> {unlinkRow.date || ''}</div>
                        <div><strong>Description:</strong> {unlinkRow.description || ''}</div>
                        <div><strong>Amount:</strong> {formatCurrency(Math.abs(Number(unlinkRow.amount||0)))}</div>
                      </div>
                    )}
                    <div className="d-flex justify-content-end gap-2" style={{ marginTop:16 }}>
                      <button className="btn btn-secondary" onClick={()=> setShowUnlinkModal(false)} disabled={unlinkLoading}>Cancel</button>
                      <button className="btn btn-warning" onClick={async ()=>{
                        if (!unlinkRow) return; setUnlinkLoading(true); setUnlinkError('');
                        try {
                          if (unlinkRow.sourceType === 'revolver') {
                            await axios.post(`/api/balance_sheet/financing/revolvers/transactions/${unlinkRow.id}/unlink-asset-expense/`);
                            setRvTxns(prev => prev.map(t => t.id===unlinkRow.id ? { ...t, linked_asset_id:null, linked_asset_expense_id:null } : t));
                          } else {
                            await axios.post(`/api/balance_sheet/assets/cash_accounts/transactions/${unlinkRow.id}/unlink-asset-expense/`);
                            setCaTxns(prev => prev.map(t => t.id===unlinkRow.id ? { ...t, linked_asset_id:null, linked_asset_expense_id:null } : t));
                          }
                          setShowUnlinkModal(false);
                          setUnlinkRow(null);
                          setChartsRefresh(v=>v+1);
                        } catch (e) {
                          setUnlinkError('Failed to unlink transaction.');
                        } finally {
                          setUnlinkLoading(false);
                        }
                      }} disabled={unlinkLoading}>
                        {unlinkLoading ? 'Unlinking…' : 'Unlink'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Property tab panel */}
            {activeTab === 'property' && (
            <div style={{ marginLeft: '12px', marginRight: '12px', marginBottom: '20px' }}>
                {chartType === 'summary' ? (
                  <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:12, color:'#e5e7eb' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Summary</div>
                    <PropertySummaryCard
                      property={resiProperty}
                      onChangePhoto={handlePhotoFileChange}
                      uploading={photoUploading}
                      error={photoError}
                    />
                  </div>
                ) : chartType === 'expenses' ? (
                  <>
                    <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                      <button
                        className={`btn btn-sm ${expensesSubView === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setExpensesSubView('all')}
                      >
                        All
                      </button>
                      <button
                        className={`btn btn-sm ${expensesSubView === 'recurring' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setExpensesSubView('recurring')}
                      >
                        Recurring
                      </button>
                    </div>
                    {expensesSubView === 'recurring' ? (
                      <RecurringManager
                        assetId={resiProperty?.id}
                        isProperty={true}
                        structures={resiProperty?.structures || []}
                        land={resiProperty?.land || null}
                        initialSelectedName={selectedRecurringName}
                      />
                    ) : (
                      <>
                        {(() => {
                          const assetIds = [
                            resiProperty.id, // include property itself so expenses linked to parent are shown
                            ...(Array.isArray(resiProperty.structures) ? resiProperty.structures.map(s => s.id) : []),
                            ...(resiProperty.land && resiProperty.land.id ? [resiProperty.land.id] : []),
                          ];
                          return assetIds.length ? (
                            <AggregatedAssetExpenseChart
                              key={`${chartsRefresh}-${showExpected}`}
                              assetIds={assetIds}
                              onRangeChange={(from,to)=>{ setTxFrom(from); setTxTo(to); }}
                              // Prefer AssetExpense categories (e.g., financing_interest, taxes) over txn categories like 'payments'
                              preferAssetExpenseTypes={true}
                              showExpected={false}
                              onToggleExpected={()=>{ /* disabled */ }}
                            />
                          ) : (
                            <div className="alert alert-info">No structures or land linked to this property yet.</div>
                          );
                        })()}
                        {renderTransactionsTable()}
                      </>
                    )}
                  </>
                ) : chartType === 'utilities' ? (
                  <>
                    <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                      <button
                        className={`btn btn-sm ${utilitiesSubView === 'regular' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setUtilitiesSubView('regular')}
                      >
                        Regular
                      </button>
                      <button
                        className={`btn btn-sm ${utilitiesSubView === 'detailed' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setUtilitiesSubView('detailed')}
                      >
                        Detailed
                      </button>
                    </div>
                    {utilitiesSubView === 'detailed' ? (
                      <UtilitiesDetailedChart key={chartsRefresh} propertyId={resiProperty.id} onRangeChange={(from,to)=>{ setTxFrom(from); setTxTo(to); }} />
                    ) : (
                      (() => {
                        const assetIds = [
                          resiProperty.id,
                          ...(Array.isArray(resiProperty.structures) ? resiProperty.structures.map(s => s.id) : []),
                          ...(resiProperty.land && resiProperty.land.id ? [resiProperty.land.id] : []),
                        ];
                        return (
                          <>
                            <UtilitiesComparisonChart key={chartsRefresh} propertyId={resiProperty.id} assetIds={assetIds} paidTxns={filteredTxns} onRangeChange={(from,to)=>{ setTxFrom(from); setTxTo(to); }} />
                            {/* Show the transactions table below the Utilities chart, matching Expenses UX */}
                            {renderTransactionsTable()}
                          </>
                        );
                      })()
                    )}
                  </>
                ) : chartType === 'tax' ? (
                  <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:12, color:'#e5e7eb' }}>
                    <div style={{ fontWeight: 600, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      Tax Assessments
                    </div>
                    {taxLoading ? (
                      <div style={{ color:'#9ca3af' }}>Loading…</div>
                    ) : taxAssessments.length === 0 ? (
                      <div style={{ color:'#9ca3af' }}>No tax assessments recorded for this property.</div>
                    ) : (
                      <>
                        {/* Tax Assessment Graph */}
                        <div style={{ marginBottom: 20 }}>
                          <Plot
                            data={[
                              {
                                x: taxAssessments.map(a => a.assessment_year),
                                y: taxAssessments.map(a => a.total_assessed_value),
                                type: 'scatter', mode: 'lines+markers', name: 'Total Assessed',
                                line: { color: '#60a5fa' }
                              },
                              {
                                x: taxAssessments.map(a => a.assessment_year),
                                y: taxAssessments.map(a => a.total_taxable_value),
                                type: 'scatter', mode: 'lines+markers', name: 'Total Taxable',
                                line: { color: '#fbbf24' }
                              },
                              {
                                x: taxAssessments.map(a => a.assessment_year),
                                y: taxAssessments.map(a => a.net_annual_taxes),
                                type: 'scatter', mode: 'lines+markers', name: 'Net Annual Taxes',
                                line: { color: '#10b981' }, yaxis: 'y2'
                              }
                            ]}
                            layout={{
                              paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                              margin: { l: 60, r: 60, t: 20, b: 40 },
                              xaxis: { title: 'Assessment Year', color: '#9ca3af', gridcolor: '#1f2937', tickmode: 'linear' },
                              yaxis: { title: 'Property Value ($)', color: '#9ca3af', gridcolor: '#1f2937' },
                              yaxis2: { title: 'Taxes ($)', color: '#10b981', overlaying: 'y', side: 'right', showgrid: false },
                              legend: { font: { color: '#e5e7eb' }, orientation: 'h', x: 0.5, xanchor: 'center', y: -0.2 },
                              hovermode: 'x unified'
                            }}
                            style={{ width: '100%', height: '350px' }}
                            config={{ displayModeBar: false }}
                          />
                        </div>

                        {/* Tax Assessment Table */}
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #374151', color: '#9ca3af', textAlign: 'left' }}>
                                <th style={{ padding: '8px 4px', cursor: 'pointer' }} onClick={() => handleTaxSort('assessment_year')}>
                                  Year {renderSortIcon('assessment_year')}
                                </th>
                                <th style={{ padding: '8px 4px', cursor: 'pointer' }} onClick={() => handleTaxSort('tax_statement_year')}>
                                  Statement {renderSortIcon('tax_statement_year')}
                                </th>
                                <th style={{ padding: '8px 4px', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleTaxSort('total_assessed_value')}>
                                  Assessed Total {renderSortIcon('total_assessed_value')}
                                </th>
                                <th style={{ padding: '8px 4px', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleTaxSort('total_taxable_value')}>
                                  Taxable Total {renderSortIcon('total_taxable_value')}
                                </th>
                                <th style={{ padding: '8px 4px', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleTaxSort('total_rollback_rate')}>
                                  Rollback {renderSortIcon('total_rollback_rate')}
                                </th>
                                <th style={{ padding: '8px 4px', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleTaxSort('levy_rate')}>
                                  Levy Rate {renderSortIcon('levy_rate')}
                                </th>
                                <th style={{ padding: '8px 4px', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleTaxSort('net_taxable_value')}>
                                  Net Taxable {renderSortIcon('net_taxable_value')}
                                </th>
                                <th style={{ padding: '8px 4px', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleTaxSort('net_annual_taxes')}>
                                  Net Taxes {renderSortIcon('net_annual_taxes')}
                                </th>
                                <th style={{ padding: '8px 4px', textAlign: 'center' }}>Link</th>
                                <th style={{ padding: '8px 4px', textAlign: 'center' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedTaxAssessments.map(a => (
                                <tr key={a.id} style={{ borderBottom: '1px solid #1f2937' }}>
                                  <td style={{ padding: '8px 4px' }}>{a.assessment_year}</td>
                                  <td style={{ padding: '8px 4px' }}>{a.tax_statement_year}</td>
                                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>{formatCurrency(a.total_assessed_value)}</td>
                                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>{formatCurrency(a.total_taxable_value)}</td>
                                  <td style={{ padding: '8px 4px', textAlign: 'right', color: '#10b981' }}>{a.total_rollback_rate ? a.total_rollback_rate.toFixed(6) : '0.000000'}</td>
                                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>{parseFloat(a.levy_rate).toFixed(7)}</td>
                                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>{formatCurrency(a.net_taxable_value)}</td>
                                  <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{formatCurrency(a.net_annual_taxes)}</td>
                                  <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                                    {a.county_url ? (
                                      <a href={a.county_url} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontSize: '1.2rem' }}>
                                        🔗
                                      </a>
                                    ) : '—'}
                                  </td>
                                  <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                                    <button 
                                      className="btn btn-xs btn-outline-secondary" 
                                      onClick={() => { setEditingAssessment(a); setShowTaxModal(true); }}
                                    >Edit</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                ) : chartType === 'debt_equity' ? (
                  <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:12, color:'#e5e7eb' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Debt & Equity</div>
                    {debtEqLoading ? (
                      <div style={{ color:'#9ca3af' }}>Loading…</div>
                    ) : debtEqError ? (
                      <div className="alert alert-warning">{debtEqError}</div>
                    ) : (debtEqSeries.market.length === 0 && debtEqSeries.debt.length === 0) ? (
                      <div style={{ color:'#9ca3af' }}>No market/debt series available yet.</div>
                    ) : (
                      (() => {
                        const alignSeries = (market, debt, debtRaw) => {
                          // Build union of date keys
                          const keys = new Set();
                          for (const p of market) keys.add(p.x.toISOString().slice(0,10));
                          for (const p of debt) keys.add(p.x.toISOString().slice(0,10));
                          const dates = Array.from(keys).sort();
                          // Pointers for forward-fill
                          let mi = 0, di = 0, dri = 0; let mv = null, dv = null; let mortv = null, helv = null;
                          const mOut = []; const dOut = []; const xOut = []; const mortOut = []; const helOut = [];
                          for (const iso of dates) {
                            const dObj = new Date(iso + 'T00:00:00Z');
                            while (mi < market.length && market[mi].x.toISOString().slice(0,10) <= iso) { mv = market[mi].y; mi++; }
                            while (di < debt.length && debt[di].x.toISOString().slice(0,10) <= iso) { dv = debt[di].y; di++; }
                            while (dri < (debtRaw?.length||0) && debtRaw[dri].x.toISOString().slice(0,10) <= iso) { mortv = debtRaw[dri].mortgage; helv = debtRaw[dri].heloc; dri++; }
                            if (mv == null && market.length) mv = market[0].y; // fallback to first
                            if (dv == null && debt.length) dv = debt[0].y;
                            if (mortv == null && (debtRaw?.length||0)) mortv = debtRaw[0].mortgage;
                            if (helv == null && (debtRaw?.length||0)) helv = debtRaw[0].heloc;
                            xOut.push(dObj); mOut.push(mv != null ? mv : 0); dOut.push(dv != null ? dv : 0);
                            mortOut.push(mortv != null ? mortv : 0); helOut.push(helv != null ? helv : 0);
                          }
                          // Ensure last point is today if either series last point is today
                          return { x: xOut, m: mOut, d: dOut, mort: mortOut, hel: helOut };
                        };

                        const { x, m, d, mort, hel } = alignSeries(debtEqSeries.market, debtEqSeries.debt, debtEqSeries.debtRaw);
                        const equity = m.map((mv, idx) => Math.max(0, mv - (d[idx] || 0)));

                        const traces = [];
                        // Invisible baseline for fill-between (debt as baseline)
                        traces.push({
                          x, y: d,
                          type: 'scatter', mode: 'lines',
                          line: { color: 'rgba(0,0,0,0)' },
                          hoverinfo: 'skip', showlegend: false,
                        });
                        // Equity filled area between debt and market
                        traces.push({
                          x, y: m,
                          type: 'scatter', mode: 'lines',
                          name: 'Equity',
                          fill: 'tonexty',
                          fillcolor: 'rgba(34,197,94,0.25)', // green-500 at 25%
                          line: { color: 'rgba(34,197,94,0.0)' },
                          hoveron: 'fills+points',
                          hovertemplate: '%{x|%Y-%m-%d}<br>Equity: $%{customdata:,.2f}<extra></extra>',
                          customdata: equity,
                        });
                        // Debt line
                        traces.push({
                          x, y: d,
                          type: 'scatter', mode: 'lines+markers',
                          name: 'Outstanding Debt',
                          line: { color: '#f59e0b', width: 2 }, // switched: use amber for Debt
                          marker: { size: 4 },
                          customdata: mort.map((mv, i) => [mv, hel[i]]),
                          hovertemplate: '%{x|%Y-%m-%d}<br>Outstanding Debt: $%{y:,.2f}<br>Mortgage: $%{customdata[0]:,.2f}<br>HELOC: $%{customdata[1]:,.2f}<extra></extra>'
                        });
                        // Market line
                        traces.push({
                          x, y: m,
                          type: 'scatter', mode: 'lines+markers',
                          name: 'Market Value',
                          line: { color: '#10b981', width: 2 }, // switched: use emerald for Market
                          marker: { size: 4 },
                          hovertemplate: '%{x|%Y-%m-%d}<br>Market Value: $%{y:,.2f}<extra></extra>'
                        });

                        return (
                          <Plot
                            data={traces}
                            layout={{
                              paper_bgcolor: '#0b1220',
                              plot_bgcolor: '#0b1220',
                              margin: { l: 60, r: 20, t: 40, b: 40 },
                              hovermode: 'x unified',
                              xaxis: {
                                title: 'Date',
                                color: '#cbd5e1',
                                gridcolor: '#1f2937',
                                rangeselector: {
                                  buttons: [
                                    { count: 6, label: '6m', step: 'month', stepmode: 'backward' },
                                    { count: 1, label: '1y', step: 'year', stepmode: 'backward' },
                                    { step: 'all' }
                                  ]
                                },
                                rangeslider: { visible: true },
                                type: 'date',
                              },
                              yaxis: {
                                title: 'USD',
                                color: '#cbd5e1',
                                gridcolor: '#1f2937',
                                tickprefix: '$',
                                autorange: true,
                                rangemode: 'tozero',
                                zeroline: true,
                                zerolinecolor: '#374151',
                                separatethousands: true,
                              },
                              legend: {
                                orientation: 'h', x: 0.5, xanchor: 'center', y: 1.15, yanchor: 'bottom',
                                font: { color: '#e5e7eb' }
                              },
                              showlegend: true,
                              autosize: true,
                            }}
                            config={{ displayModeBar: false, responsive: true }}
                            useResizeHandler={true}
                            style={{ width: '100%', height: '100%' }}
                          />
                        );
                      })()
                    )}
                  </div>
                ) : chartType === 'todo' ? (
                  <div style={{ marginTop: '12px', marginBottom: '24px' }}>
                    <PhysicalAssetToDoList assetId={resiProperty.id} includeChildren={true} assetType="home" />
                  </div>
                ) : (
                  <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:12, color:'#e5e7eb' }}>
                    {valuationLoading ? (
                      <div style={{ color:'#9ca3af' }}>Loading…</div>
                    ) : valuationError ? (
                      <div className="alert alert-warning">{valuationError}</div>
                    ) : (valuationSeries.book.length === 0 && valuationSeries.market.length === 0) ? (
                      <div style={{ color:'#9ca3af' }}>No book/market value series available yet.</div>
                    ) : (
                      <Plot
                        data={(() => {
                          const toXY = (arr) => ({
                            x: arr.map(p => p.x),
                            y: arr.map(p => p.y),
                          });
                          const book = toXY(valuationSeries.book);
                          const market = toXY(valuationSeries.market);
                          const traces = [];
                          if (book.x.length) traces.push({
                            ...book,
                            type: 'scatter',
                            mode: 'lines+markers',
                            name: 'Book Value',
                            line: { color: '#60a5fa', width: 2 },
                            marker: { size: 4 },
                            hovertemplate: '%{x|%Y-%m-%d}<br>$%{y:,.2f}<extra>Book</extra>'
                          });
                          if (market.x.length) traces.push({
                            ...market,
                            type: 'scatter',
                            mode: 'lines+markers',
                            name: 'Market Value',
                            line: { color: '#f59e0b', width: 2 },
                            marker: { size: 4 },
                            hovertemplate: '%{x|%Y-%m-%d}<br>$%{y:,.2f}<extra>Market</extra>'
                          });
                          return traces;
                        })()}
                        layout={{
                          paper_bgcolor: '#0b1220',
                          plot_bgcolor: '#0b1220',
                          // Fit legend without pushing plot too low
                          margin: { l: 60, r: 20, t: 40, b: 40 },
                          xaxis: {
                            title: 'Date',
                            color: '#cbd5e1',
                            gridcolor: '#1f2937',
                            rangeselector: {
                              buttons: [
                                { count: 6, label: '6m', step: 'month', stepmode: 'backward' },
                                { count: 1, label: '1y', step: 'year', stepmode: 'backward' },
                                { step: 'all' }
                              ]
                            },
                            rangeslider: { visible: true },
                            type: 'date',
                          },
                          yaxis: {
                            title: 'USD',
                            color: '#cbd5e1',
                            gridcolor: '#1f2937',
                            tickprefix: '$',
                            // Ensure the chart always starts at $0.0
                            autorange: true,
                            rangemode: 'tozero',
                            zeroline: true,
                            zerolinecolor: '#374151',
                            separatethousands: true,
                          },
                          // Center the legend horizontally above the chart
                          legend: {
                            orientation: 'h',
                            x: 0.5,
                            xanchor: 'center',
                            y: 1.15,
                            yanchor: 'bottom',
                            font: { color: '#e5e7eb' }
                          },
                          showlegend: true,
                          autosize: true,
                        }}
                        config={{ displayModeBar: false, responsive: true }}
                        style={{ width: '100%', height: '420px' }}
                      />
                    )}
                  </div>
                )}

                {/* Recurring management panel now lives under the "Recurring Expenses" view */}
              </div>
            )}

            {activeTab === 'structures' && (
            <div style={{ marginLeft: '12px', marginRight: '12px' }}>
              {structureFormVisible && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
                  <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(720px, 95vw)' }}>
                    <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                      <h5 style={{ margin:0 }}>Add Structure</h5>
                      <button className="btn btn-sm btn-secondary" onClick={() => setStructureFormVisible(false)} style={{ marginLeft:'auto' }}>✕</button>
                    </div>
                    <div style={{ padding:16 }}>
                      <CreateStructure onSubmit={handleStructureSubmit} propertyId={resiProperty?.id} />
                    </div>
                  </div>
                </div>
              )}

              {(resiProperty.structures && resiProperty.structures.length > 0) && (
                <div style={{ marginTop: 12, marginBottom: 12 }}>
                  {chartType === 'expenses' ? (
                    <>
                      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                        <button
                          className={`btn btn-sm ${expensesSubView === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setExpensesSubView('all')}
                        >
                          All
                        </button>
                        <button
                          className={`btn btn-sm ${expensesSubView === 'recurring' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setExpensesSubView('recurring')}
                        >
                          Recurring
                        </button>
                      </div>
                      {expensesSubView === 'recurring' ? (
                        <div className="alert alert-info">Select 'Property' tab to manage recurring expenses for the entire asset.</div>
                      ) : (
                        <AggregatedAssetExpenseChart
                          key={chartsRefresh}
                          assetIds={resiProperty.structures.map(s => s.id)}
                          chartTitle="Structure Expenses"
                          onRangeChange={(from,to)=>{ setTxFrom(from); setTxTo(to); }}
                        />
                      )}
                    </>
                  ) : chartType === 'utilities' ? (
                    <>
                      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                        <button
                          className={`btn btn-sm ${utilitiesSubView === 'regular' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setUtilitiesSubView('regular')}
                        >
                          Regular
                        </button>
                        <button
                          className={`btn btn-sm ${utilitiesSubView === 'detailed' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setUtilitiesSubView('detailed')}
                        >
                          Detailed
                        </button>
                      </div>
                      {utilitiesSubView === 'detailed' ? (
                        <UtilitiesDetailedChart key={chartsRefresh} propertyId={resiProperty.id} onRangeChange={(from,to)=>{ setTxFrom(from); setTxTo(to); }} />
                      ) : (
                        <UtilitiesComparisonChart
                          key={chartsRefresh}
                          propertyId={resiProperty.id}
                          assetIds={resiProperty.structures.map(s => s.id)}
                          paidTxns={filteredTxns}
                          onRangeChange={(from,to)=>{ setTxFrom(from); setTxTo(to); }}
                        />
                      )}
                    </>
                  ) : chartType === 'todo' ? (
                    <div style={{ marginTop: '12px', marginBottom: '24px' }}>
                      {resiProperty.structures && resiProperty.structures.map(s => (
                        <PhysicalAssetToDoList key={s.id} assetId={s.id} titleOverride={`To-Do List: ${s.description}`} />
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
              {chartType === 'expenses' && renderTransactionsTable()}
            </div>
            )}

            {activeTab === 'land' && (
            <div>
                <div style={{marginLeft: '12px', marginRight: '12px', marginBottom: '20px'}}>
                    {!resiProperty.land ? (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setLandFormVisible(prev => !prev)}
                        >
                            {landFormVisible ? "Cancel Land Form" : "Add Land"}
                        </button>
                    ) : null}

                    {landFormVisible && (
                                            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
                                              <div style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', borderRadius:8, width:'min(720px, 95vw)' }}>
                                                <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                                                  <h5 style={{ margin:0 }}>Add Land</h5>
                                                  <button className="btn btn-sm btn-secondary" onClick={() => setLandFormVisible(false)} style={{ marginLeft:'auto' }}>✕</button>
                                                </div>
                                                <div style={{ padding:16 }}>
                                                  <CreateLand onSubmit={handleLandSubmit} />
                                                </div>
                                              </div>
                                            </div>
                                        )}

                    {showUtilityLinkModal && utilityRow && (
                      <AssetExpenseLinkModal
                        visible={showUtilityLinkModal}
                        onClose={()=> { setShowUtilityLinkModal(false); setUtilityRow(null); }}
                        sourceType={utilityRow.sourceType === 'revolver' ? 'revolver' : 'cash'}
                        transaction={{
                          id: utilityRow.id,
                          sourceType: utilityRow.sourceType,
                          description: utilityRow.description,
                          amount: utilityRow.amount,
                          transaction_date: utilityRow.transaction_date || utilityRow.date || '',
                          post_date: utilityRow.post_date || '',
                          date: utilityRow.transaction_date || utilityRow.post_date || utilityRow.date || '',
                          linked_asset_id: utilityRow.linked_asset_id || '',
                          linked_asset_expense_id: (utilityRow.linked_asset_expense_id != null && utilityRow.linked_asset_expense_id !== '') ? String(utilityRow.linked_asset_expense_id) : '',
                          linked_utility_bill_id: utilityRow.linked_utility_bill_id || '',
                          linked_recurring_series_id: utilityRow.linked_recurring_series_id || '',
                          linked_property_id: utilityRow.linked_property_id || resiProperty.id || '',
                          expense_category: utilityRow.expense_category || '',
                        }}
                        domain={getDomainForCategory(utilityRow.expense_category)}
                        utilitiesConfig={{
                          propertyId: resiProperty.id,
                          categories: [
                            { value: 'utilities_electric_gas', label: 'Electric & Gas' },
                            { value: 'utilities_water', label: 'Water' },
                            { value: 'utilities_trash', label: 'Trash/Waste' },
                            { value: 'utilities_sewer', label: 'Sewer' },
                            { value: 'utilities_internet', label: 'Internet' },
                            { value: 'utilities_cable_tv', label: 'Cable/TV' },
                            { value: 'utilities_phone', label: 'Phone' },
                          ],
                        }}
                        initialValues={{
                          asset_id: utilityRow.linked_asset_id || '',
                          expense_category: utilityRow.expense_category || '',
                          linked_asset_expense_id: (utilityRow.linked_asset_expense_id != null && utilityRow.linked_asset_expense_id !== '') ? String(utilityRow.linked_asset_expense_id) : '',
                          property_id: (()=>{ try {
                            const aid = utilityRow.linked_asset_id;
                            if (!aid) return utilityRow.linked_property_id || resiProperty.id || '';
                            const asset = assetMaps?.byId?.get ? assetMaps.byId.get(String(aid)) : null;
                            if (!asset) return utilityRow.linked_property_id || resiProperty.id || '';
                            const t2 = String(asset?.asset_type_2 || asset?.type_2 || asset?.type2 || '').toLowerCase();
                            if (t2 === 'resi_property') return String(asset.id);
                            if (t2 === 'structure' || t2 === 'land') {
                              const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent || null;
                              const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
                              return rpId ? String(rpId) : (utilityRow.linked_property_id || resiProperty.id || '');
                            }
                            return utilityRow.linked_property_id || resiProperty.id || '';
                          } catch(e){ return utilityRow.linked_property_id || resiProperty.id || ''; } })(),
                          recurring_series_id: utilityRow.linked_recurring_series_id || '',
                          utility_bill_id: utilityRow.linked_utility_bill_id || '',
                        }}
                        onSaved={async (payload)=>{
                          const ae = payload || {};
                          const updateFn = (prev) => prev.map(t => {
                            if (t.id === utilityRow.id && t.sourceType === (utilityRow.sourceType === 'revolver' ? 'revolver' : 'cash')) {
                              return { 
                                ...t, 
                                linked_asset_id: ae.asset_id || t.linked_asset_id,
                                linked_asset_expense_id: ae.asset_expense_id || t.linked_asset_expense_id,
                                linked_recurring_series_id: ae.recurring_series_id || t.linked_recurring_series_id,
                                linked_utility_bill_id: ae.utility_bill_id || t.linked_utility_bill_id,
                              };
                            }
                            return t;
                          });
                          setRvTxns(updateFn);
                          setCaTxns(updateFn);
                          
                          // Refresh transactions
                          if (utilityRow.sourceType === 'cash') {
                              const caRes = await axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: txFrom, end_date: txTo } });
                              setCaTxns(caRes.data || []);
                          } else if (utilityRow.sourceType === 'revolver') {
                              const rvRes = await axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: txFrom, end_date: txTo } });
                              setRvTxns(rvRes.data || []);
                          }

                          setShowUtilityLinkModal(false);
                          setUtilityRow(null);
                          setChartsRefresh(prev => prev + 1);
                        }}
                        onUnlinked={async ()=>{
                          // Refresh transactions
                          if (utilityRow.sourceType === 'cash') {
                              const caRes = await axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: txFrom, end_date: txTo } });
                              setCaTxns(caRes.data || []);
                          } else if (utilityRow.sourceType === 'revolver') {
                              const rvRes = await axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: txFrom, end_date: txTo } });
                              setRvTxns(rvRes.data || []);
                          }
                          setShowUtilityLinkModal(false);
                          setUtilityRow(null);
                          setChartsRefresh(prev => prev + 1);
                        }}
                      />
                    )}

                    {showDebtModal && linkingRow && (
                        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
                            <div style={{ width: '720px', maxWidth: '95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
                                <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                                    <div style={{ fontSize:18, fontWeight:700 }}>Link Payment to Debt</div>
                                    <button onClick={()=>{ setShowDebtModal(false); setLinkingRow(null); }} style={{ marginLeft:'auto' }} className="btn btn-sm btn-secondary">✕</button>
                                </div>
                                <div style={{ padding:16 }}>
                                    {modalError && <div className="alert alert-danger">{modalError}</div>}
                                    {modalPhase === 'chooseDebt' ? (
                                        <>
                                            <div style={{ marginBottom:12 }}>Select the loan/mortgage this payment belongs to:</div>
                                            <div style={{ maxHeight:'40vh', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                                                <table style={{ width:'100%' }}>
                                                    <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                                                        <tr>
                                                            <th style={{ textAlign:'left', padding:8 }}>Debt Name</th>
                                                            <th style={{ textAlign:'left', padding:8 }}>Lender</th>
                                                            <th style={{ textAlign:'right', padding:8 }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {debtList.map(d => (
                                                            <tr key={d.id} style={{ borderBottom:'1px solid #1f2937' }}>
                                                                <td style={{ padding:8 }}>{d.debt_type.replace('_',' ').toUpperCase()}</td>
                                                                <td style={{ padding:8 }}>{d.lender_name || 'N/A'}</td>
                                                                <td style={{ padding:8, textAlign:'right' }}>
                                                                    <button className="btn btn-sm btn-primary" onClick={() => { setSelectedDebt(d); loadDebtCandidates(d); }}>Select</button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ marginBottom:12 }}>
                                                Matching {selectedDebt.debt_type.replace('_',' ')} candidates for <strong>{linkingRow.description}</strong> ({formatCurrency(linkingRow.amount)}):
                                            </div>
                                            <div style={{ maxHeight:'40vh', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                                                <table style={{ width:'100%' }}>
                                                    <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                                                        <tr>
                                                            <th style={{ textAlign:'left', padding:8 }}>Date</th>
                                                            <th style={{ textAlign:'right', padding:8 }}>Amount</th>
                                                            <th style={{ textAlign:'right', padding:8 }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {debtCandidates.map(c => (
                                                            <tr key={c.id} style={{ borderBottom:'1px solid #1f2937' }}>
                                                                <td style={{ padding:8 }}>{c.payment_date}</td>
                                                                <td style={{ padding:8, textAlign:'right' }}>{formatCurrency(c.total_amount)}</td>
                                                                <td style={{ padding:8, textAlign:'right' }}>
                                                                    <button className="btn btn-sm btn-success" onClick={() => confirmLinkToCandidate(c)}>Link</button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {debtCandidates.length === 0 && (
                                                            <tr><td colSpan={3} style={{ padding:12, textAlign:'center', color:'#9ca3af' }}>No matching payments found in the amortization schedule.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <button className="btn btn-link btn-sm mt-3" style={{ color:'#93c5fd' }} onClick={() => setModalPhase('chooseDebt')}>← Back to debt list</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Show only the land graph, styled like the Property tab */}
                    {resiProperty.land ? (
                      chartType === 'expenses' ? (
                        <div style={{ marginTop: 12, marginBottom: 12 }}>
                          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                            <button
                              className={`btn btn-sm ${expensesSubView === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setExpensesSubView('all')}
                            >
                              All
                            </button>
                            <button
                              className={`btn btn-sm ${expensesSubView === 'recurring' ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setExpensesSubView('recurring')}
                            >
                              Recurring
                            </button>
                          </div>
                          {expensesSubView === 'recurring' ? (
                            <div className="alert alert-info">Select 'Property' tab to manage recurring expenses for the entire asset.</div>
                          ) : (
                            <AggregatedAssetExpenseChart key={chartsRefresh} assetIds={[resiProperty.land.id]} chartTitle="Land Expenses" onRangeChange={(from,to)=>{ setTxFrom(from); setTxTo(to); }} showExpected={false} onToggleExpected={()=>{}} />
                          )}
                        </div>
                      ) : chartType === 'utilities' ? (
                        <div style={{ marginTop: 12, marginBottom: 12 }}>
                          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                            <button
                              className={`btn btn-sm ${utilitiesSubView === 'regular' ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setUtilitiesSubView('regular')}
                            >
                              Regular
                            </button>
                            <button
                              className={`btn btn-sm ${utilitiesSubView === 'detailed' ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setUtilitiesSubView('detailed')}
                            >
                              Detailed
                            </button>
                          </div>
                          {utilitiesSubView === 'detailed' ? (
                            <UtilitiesDetailedChart key={chartsRefresh} propertyId={resiProperty.id} onRangeChange={(from,to)=>{ setTxFrom(from); setTxTo(to); }} />
                          ) : (
                            <UtilitiesComparisonChart
                              key={chartsRefresh}
                              propertyId={resiProperty.id}
                              assetIds={[resiProperty.land.id]}
                              paidTxns={filteredTxns}
                              showBilled={false}
                              onRangeChange={(from,to)=>{ setTxFrom(from); setTxTo(to); }}
                            />
                          )}
                        </div>
                      ) : chartType === 'todo' ? (
                          <div style={{ marginTop: 12, marginBottom: 12 }}>
                            <PhysicalAssetToDoList assetId={resiProperty.land.id} titleOverride={`To-Do List: ${resiProperty.land.description}`} />
                          </div>
                        ) : chartType === 'tax' ? (
                        <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:12, color:'#e5e7eb' }}>
                          <div style={{ fontWeight: 600, marginBottom: 6 }}>Tax Assessment</div>
                          <div style={{ color:'#9ca3af' }}>No tax assessment chart available for land.</div>
                        </div>
                      ) : (
                        <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:12, color:'#e5e7eb' }}>
                          <div style={{ fontWeight: 600, marginBottom: 6 }}>Book & Market Values</div>
                          <div style={{ color:'#9ca3af' }}>No book/market value series available for land.</div>
                        </div>
                      )
                    ) : null}
                </div>
                {chartType === 'expenses' && (
                  <div>
                    {renderTransactionsTable()}
                  </div>
                )}
            </div>
            )}

            {showUtilityLinkModal && utilityRow && (
              <AssetExpenseLinkModal
                visible={showUtilityLinkModal}
                onClose={()=> { setShowUtilityLinkModal(false); setUtilityRow(null); }}
                sourceType={utilityRow.sourceType}
                transaction={utilityRow}
                contextLabel={'Utilities'}
                domain={'utilities'}
                utilitiesConfig={{
                  propertyId: resiProperty?.id || null,
                  categories: [
                    { value: 'utilities_electric_gas', label: 'Electric & Gas' },
                    { value: 'utilities_water', label: 'Water' },
                    { value: 'utilities_trash', label: 'Trash/Waste' },
                    { value: 'utilities_sewer', label: 'Sewer' },
                    { value: 'utilities_internet', label: 'Internet' },
                    { value: 'utilities_cable_tv', label: 'Cable/TV' },
                    { value: 'utilities_phone', label: 'Phone' },
                  ]
                }}
                assetOptions={(resiProperty?.structures||[]).map((s, i)=> ({ id: s.id, label: `${resiProperty?.address?.street_address || ''} - ${(s.description||'Structure')} - Structure ${i+1}` }))}
                initialValues={{
                  asset_id: (()=>{
                    // Prefer the asset already linked on the row; otherwise default to first structure
                    if (utilityRow?.linked_asset_id) return utilityRow.linked_asset_id;
                    return (resiProperty?.structures && resiProperty.structures[0]?.id) ? resiProperty.structures[0].id : '';
                  })(),
                  recurring: (()=>{ try { const k = `${utilityRow.sourceType}-${utilityRow.id}`; return !!linkedMeta[k]?.recurring; } catch(e){ return undefined; } })(),
                  frequency: (()=>{ try { const k = `${utilityRow.sourceType}-${utilityRow.id}`; return linkedMeta[k]?.frequency || undefined; } catch(e){ return undefined; } })(),
                  utility_bill_id: (()=>{ try { const k = `${utilityRow.sourceType}-${utilityRow.id}`; return linkedExtras[k]?.utility_bill_id || utilityRow?.linked_utility_bill_id || ''; } catch(e){ return utilityRow?.linked_utility_bill_id || ''; } })(),
                  recurring_series_id: (()=>{ try { const k = `${utilityRow.sourceType}-${utilityRow.id}`; return linkedExtras[k]?.recurring_series_id || utilityRow?.linked_recurring_series_id || ''; } catch(e){ return utilityRow?.linked_recurring_series_id || ''; } })(),
                }}
                onSaved={async ({ utility_bill_id, asset_expense_id, recurring, recurring_frequency, recurring_type, recurring_series_id })=>{
                  try {
                    setChartsRefresh(prev => prev + 1);
                    // Persist linked utility bill id locally for prepopulation on next edit
                    if (utility_bill_id != null) {
                      if (utilityRow.sourceType === 'revolver') {
                        setRvTxns(prev => prev.map(t => t.id === utilityRow.id ? { ...t, linked_utility_bill_id: Number(utility_bill_id) } : t));
                      } else {
                        setCaTxns(prev => prev.map(t => t.id === utilityRow.id ? { ...t, linked_utility_bill_id: Number(utility_bill_id) } : t));
                      }
                    }
                    // Persist linked asset expense id (idempotent upsert backend)
                    if (asset_expense_id != null) {
                      if (utilityRow.sourceType === 'revolver') {
                        setRvTxns(prev => prev.map(t => t.id === utilityRow.id ? { ...t, linked_asset_expense_id: Number(asset_expense_id) } : t));
                      } else {
                        setCaTxns(prev => prev.map(t => t.id === utilityRow.id ? { ...t, linked_asset_expense_id: Number(asset_expense_id) } : t));
                      }
                    }
                    // Persist linked recurring series id for prepopulation
                    if (recurring_series_id != null) {
                      if (utilityRow.sourceType === 'revolver') {
                        setRvTxns(prev => prev.map(t => t.id === utilityRow.id ? { ...t, linked_recurring_series_id: Number(recurring_series_id) } : t));
                      } else {
                        setCaTxns(prev => prev.map(t => t.id === utilityRow.id ? { ...t, linked_recurring_series_id: Number(recurring_series_id) } : t));
                      }
                    }
                    // Update local extras cache for stable prepopulation across refetches
                    try {
                      const k = `${utilityRow.sourceType}-${utilityRow.id}`;
                      setLinkedExtras(prev => ({
                        ...prev,
                        [k]: {
                          utility_bill_id: (utility_bill_id != null ? Number(utility_bill_id) : (prev[k]?.utility_bill_id ?? null)),
                          recurring_series_id: (recurring_series_id != null ? Number(recurring_series_id) : (prev[k]?.recurring_series_id ?? null))
                        }
                      }));
                    } catch (e) { /* ignore */ }
                    // Update cached recurring metadata for sorting/columns
                    try {
                      const k = `${utilityRow.sourceType}-${utilityRow.id}`;
                      setLinkedMeta(prev => ({ ...prev, [k]: { recurring: !!recurring, frequency: recurring ? (recurring_frequency || prev[k]?.frequency || '') : '' } }));
                    } catch (e) { /* ignore */ }
                    if (txFrom && txTo) {
                      const [rvRes, caRes] = await Promise.allSettled([
                        axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: txFrom, end_date: txTo } }),
                        axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: txFrom, end_date: txTo } }),
                      ]);
                      const rvList = (rvRes.status === 'fulfilled') ? (Array.isArray(rvRes.value?.data) ? rvRes.value.data : []) : [];
                      const caList = (caRes.status === 'fulfilled') ? (Array.isArray(caRes.value?.data) ? caRes.value.data : []) : [];
                      setRvTxns(rvList);
                      setCaTxns(caList);
                    }
                  } catch (e) { /* ignore */ }
                }}
                onUnlinked={async ()=>{
                  try {
                    setChartsRefresh(prev => prev + 1);
                    if (txFrom && txTo) {
                      const [rvRes, caRes] = await Promise.allSettled([
                        axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: txFrom, end_date: txTo } }),
                        axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: txFrom, end_date: txTo } }),
                      ]);
                      const rvList = (rvRes.status === 'fulfilled') ? (Array.isArray(rvRes.value?.data) ? rvRes.value.data : []) : [];
                      const caList = (caRes.status === 'fulfilled') ? (Array.isArray(caRes.value?.data) ? caRes.value.data : []) : [];
                      setRvTxns(rvList);
                      setCaTxns(caList);
                    }
                  } catch (e) { /* ignore */ }
                }}
              />
            )}

            {showHomeModal && (
              <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }} onClick={()=> setShowHomeModal(false)}>
                <div style={{ width:'min(720px, 95vw)', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }} onClick={(e)=> e.stopPropagation()}>
                  <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                    <div style={{ fontSize:18, fontWeight:700 }}>Link Home Expense</div>
                    <button className="btn btn-sm btn-secondary" onClick={()=> setShowHomeModal(false)} style={{ marginLeft:'auto' }}>✕</button>
                  </div>
                  <div style={{ padding:16 }}>
                    {homeError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{homeError}</div>}
                    <div className="mb-3">
                      <label className="form-label">Residential Property</label>
                      <input className="form-control" value={`${resiProperty?.address?.street_address || ''}, ${resiProperty?.address?.city || ''}, ${resiProperty?.address?.state || ''}`} disabled />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Structure</label>
                      <select className="form-select" value={homeSelectedAssetId} onChange={(e)=> setHomeSelectedAssetId(e.target.value)}>
                        <option value="">Select structure…</option>
                        {homeAssetOptions.map(opt => (<option key={opt.id} value={opt.id}>{opt.label}</option>))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-check">
                        <input className="form-check-input" type="checkbox" checked={homeIsRecurring} onChange={(e)=> setHomeIsRecurring(e.target.checked)} />
                        <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                      </label>
                      {homeIsRecurring && (
                        <div style={{ marginTop:8 }}>
                          <div className="btn-group btn-group-sm" role="group" aria-label="Recurring mode">
                            <button type="button" className={`btn ${homeRecurringMode==='existing' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=> setHomeRecurringMode('existing')}>Existing</button>
                            <button type="button" className={`btn ${homeRecurringMode==='new' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=> setHomeRecurringMode('new')}>New</button>
                          </div>
                          {homeRecurringMode === 'existing' && (
                            <div style={{ marginTop:8 }}>
                              <label className="form-label">Existing recurring series</label>
                              <select className="form-select" value={homeExistingSeriesName} onChange={(e)=> setHomeExistingSeriesName(e.target.value)} disabled={!homeSelectedAssetId}>
                                <option value="">-- Select series --</option>
                                {homeSeriesOptions.map(s => (
                                  <option key={s.name} value={s.name}>{s.name} {s.type ? `(${s.type})` : ''}</option>
                                ))}
                              </select>
                              {!homeSeriesOptions.length && <small className="form-text text-muted">No existing recurring series found for this structure.</small>}
                            </div>
                          )}
                          {homeRecurringMode === 'new' && (
                            <div style={{ marginTop:8 }}>
                              <div className="mb-2">
                                <label className="form-label">New series name</label>
                                <input className="form-control" type="text" value={homeRecurringName} onChange={(e)=> setHomeRecurringName(e.target.value)} placeholder="e.g., Pest Control - Quarterly" />
                              </div>
                              <div className="mb-2">
                                <label className="form-label">Frequency</label>
                                <select className="form-select" value={homeFrequency} onChange={(e)=> setHomeFrequency(e.target.value)}>
                                  <option value="weekly">Weekly</option>
                                  <option value="monthly">Monthly</option>
                                  <option value="bi_monthly">Bi-monthly</option>
                                  <option value="quarterly">Quarterly</option>
                                  <option value="semi_annually">Semi-annually</option>
                                  <option value="annually">Annually</option>
                                </select>
                              </div>
                              <div>
                                <label className="form-label">Series type</label>
                                <select className="form-select" value={homeRecurringType} onChange={(e)=> setHomeRecurringType(e.target.value)}>
                                  <option value="fixed">Fixed</option>
                                  <option value="variable">Variable</option>
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      <button className="btn btn-secondary" onClick={()=> setShowHomeModal(false)} disabled={homeLoading}>Cancel</button>
                      <button className="btn btn-primary" onClick={submitHomeLink} disabled={homeLoading || !homeSelectedAssetId || !homeUiCategory}>Link Expense</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showLinkModal && (
              <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }} onClick={()=> setShowLinkModal(false)}>
                <div style={{ width:'min(600px, 95vw)', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }} onClick={(e)=> e.stopPropagation()}>
                  <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                    <div style={{ fontSize:18, fontWeight:700 }}>Link to Structure or Land</div>
                    <button className="btn btn-sm btn-secondary" onClick={()=> setShowLinkModal(false)} style={{ marginLeft:'auto' }}>✕</button>
                  </div>
                  <div style={{ padding:16 }}>
                    {linkError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{linkError}</div>}
                    <div style={{ marginBottom:8 }}>Choose an asset on this property:</div>
                    <div style={{ maxHeight: '40vh', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                      <table style={{ width:'100%' }}>
                        <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                          <tr>
                            <th style={{ textAlign:'left', padding:8 }}>Type</th>
                            <th style={{ textAlign:'left', padding:8 }}>Label</th>
                            <th style={{ textAlign:'left', padding:8 }}>Select</th>
                          </tr>
                        </thead>
                        <tbody>
                          {landId && (
                            <tr style={{ background:'#111827' }}>
                              <td style={{ padding:8 }}>Land</td>
                              <td style={{ padding:8 }}>{resiProperty.address?.street_address || ''} - Land</td>
                              <td style={{ padding:8 }}>
                                <input type="radio" name="assetChoice2" checked={String(selectedAssetId)===String(landId)} onChange={()=> setSelectedAssetId(String(landId))} />
                              </td>
                            </tr>
                          )}
                          {(resiProperty.structures||[]).map((s, i) => (
                            <tr key={`modal2-${s.id}`} style={{ background: i%2? '#0f172a':'#111827' }}>
                              <td style={{ padding:8 }}>Structure</td>
                              <td style={{ padding:8 }}>{resiProperty.address?.street_address || ''} - {(s.description||'Structure')} - Structure {i+1}</td>
                              <td style={{ padding:8 }}>
                                <input type="radio" name="assetChoice2" checked={String(selectedAssetId)===String(s.id)} onChange={()=> setSelectedAssetId(String(s.id))} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="form-check" style={{ marginTop:12 }}>
                      <input className="form-check-input" type="checkbox" id="recurringCheck2" checked={isRecurring} onChange={(e)=> setIsRecurring(e.target.checked)} />
                      <label className="form-check-label" htmlFor="recurringCheck2" style={{ marginLeft:6 }}>Recurring</label>
                    </div>
                    <div style={{ marginTop:8 }}>
                      <label className="form-label">Frequency</label>
                      <select className="form-select" value={recurringFrequency} onChange={(e)=> setRecurringFrequency(e.target.value)} disabled={!isRecurring}>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="bi_monthly">Bi-monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semi_annually">Semi-annually</option>
                        <option value="annually">Annually</option>
                      </select>
                    </div>

                    <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                      <button className="btn btn-secondary" onClick={()=> setShowLinkModal(false)} disabled={linkLoading}>Cancel</button>
                      <button className="btn btn-success" onClick={confirmLink} disabled={linkLoading || !selectedAssetId}>{linkLoading ? 'Linking…' : 'Link'}</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
    );
};

export default ResiPropertyDetail;